import { WebsocketDiagnostics } from '../realtime';

import { useState, useEffect, useMemo, useRef, type FormEvent } from 'react';
import { motion } from 'framer-motion';

import svgPaths from "../imports/svg-n7w9hotcin";
import {
  fetchAvailableTools,
  setSessionContext,
  summarizeSession,
  renameSessionByAI,
  fetchAdminCustomCodeByWebsiteId,
  saveAdminCustomCodeByWebsiteId,
  fetchAdminEmbedCodeByBlockId,
  saveAdminEmbedCodeByBlockId,
  fetchAdminBlockContentSummaryByBlockId,
  fetchAdminBlockContentFlatElementsByBlockId,
  fetchAdminBlockContentTreeElementsByBlockId,
  fetchAdminBlockContentPropertiesByBlockId,
  fetchAdminBlockContentFullByBlockId,
  fetchAdminBlockContentRawByBlockId,
  pushAdminBlockContentToWebliumByBlockId,
  fetchWebliumWebsites,
  fetchWebliumWebsitePages,
  fetchWebliumWebsiteBlocks,
  runSystemConnectionHoldTest,
  fetchSystemEnvConfig,
  checkSystemWebliumAuth
} from '../api';
import type {
  AvailableToolsListType,
  SystemConnectionHoldTestResponse,
  SystemEnvConfigResponse,
  SystemWebliumAuthCheckResponse,
  completionFunctionalToolsCallsLogType,
  SessionContextType,
  PageNameOption,
  SessionSummary,
  SessionRenameOptions,
  SessionUsageInfo,
  CustomCodeSectionKey,
  CustomCodeSectionsType,
  EmbedCodeSectionKey,
  EmbedCodeSectionsType,
  EmbedCodeDataType,
  AdminBlockContentSummaryType,
  WebliumWebsiteMentionItem,
  WebliumWebsitePageMentionItem,
  WebliumWebsiteBlockMentionItem
} from '../api.types';
import { PAGE_NAME_OPTIONS } from '../api.types';
import { useSession } from './SessionDropdown';
import CustomCodeEditor from './CustomCodeEditor';
import { highlightCustomCodeContent } from '../utils/customCodeHighlight';

declare const WEBLIUM_PORTAL_URL: string;

type SessionContextDisplay = SessionContextType;
const CUSTOM_CODE_TABS: Array<{ key: CustomCodeSectionKey; label: string }> = [
  { key: 'head', label: '<HEAD>' },
  { key: 'afterBody', label: 'After <BODY>' },
  { key: 'beforeBody', label: 'Before </BODY>' },
  { key: 'css', label: 'CSS' }
];

const EMBED_CODE_TABS: Array<{ key: EmbedCodeSectionKey; label: string }> = [
  { key: 'html', label: 'HTML' },
  { key: 'cssLess', label: 'CSS/LESS' },
  { key: 'js', label: 'JS' }
];

const NO_EMBED_BLOCK_OPTION_ID = '__no_embed_blocks__';
const NO_BLOCK_CONTENT_BLOCK_OPTION_ID = '__no_block_content_blocks__';
const NO_PUSH_BLOCK_OPTION_ID = '__no_push_blocks__';

type BlockContentDataViewKey =
  | 'flatElements'
  | 'treeElements'
  | 'properties'
  | 'fullContent'
  | 'rawContent';

const BLOCK_CONTENT_VIEW_BUTTONS: Array<{ key: BlockContentDataViewKey; label: string }> = [
  { key: 'flatElements', label: 'Flat elements list' },
  { key: 'treeElements', label: 'Tree elements list' },
  { key: 'properties', label: "Block's properties" },
  { key: 'fullContent', label: 'Full block content' },
  { key: 'rawContent', label: 'Raw block content' }
];

type CollapsibleSidebarSectionKey =
  | 'sessionInfo'
  | 'customCode'
  | 'embedCode'
  | 'blockContentInfo'
  | 'pushBlockToWeblium'
  | 'sessionContext'
  | 'sessionData'
  | 'sessionRename'
  | 'summary'
  | 'availableTools'
  | 'lastToolCalls'
  | 'statistics'
  | 'limits'
  | 'systemDiagnostics';
const COLLAPSIBLE_SIDEBAR_SECTION_CONTENT_IDS: Record<CollapsibleSidebarSectionKey, string> = {
  sessionInfo: 'session-info-section-content',
  customCode: 'custom-code-section-content',
  embedCode: 'embed-code-section-content',
  blockContentInfo: 'block-content-info-section-content',
  pushBlockToWeblium: 'push-block-to-weblium-section-content',
  sessionContext: 'session-context-section-content',
  sessionData: 'session-data-section-content',
  sessionRename: 'session-rename-section-content',
  summary: 'summary-section-content',
  availableTools: 'available-tools-section-content',
  lastToolCalls: 'last-tool-calls-section-content',
  statistics: 'statistics-section-content',
  limits: 'limits-section-content',
  systemDiagnostics: 'system-diagnostics-section-content'
};
const DEFAULT_EXPANDED_SIDEBAR_SECTIONS: CollapsibleSidebarSectionKey[] = [
  'sessionInfo',
  'sessionContext',
  'sessionData',
  'sessionRename',
  'summary',
  'lastToolCalls',
  'statistics',
  'limits'
];

const createDefaultExpandedSidebarSections = (): Set<CollapsibleSidebarSectionKey> => new Set(DEFAULT_EXPANDED_SIDEBAR_SECTIONS);

const getBlockDisplayName = (block: WebliumWebsiteBlockMentionItem): string => (
  block.label || block.name || 'Untitled block'
);

const createEmptyCustomCodeSections = (): CustomCodeSectionsType => ({
  head: '',
  afterBody: '',
  beforeBody: '',
  css: ''
});

const createEmptyEmbedCodeSections = (): EmbedCodeSectionsType => ({
  html: '',
  cssLess: '',
  js: ''
});

const getCustomCodeEditorMode = (tabKey: CustomCodeSectionKey): 'html-mixed' | 'css' => (
  tabKey === 'css' ? 'css' : 'html-mixed'
);

const getEmbedCodeEditorMode = (tabKey: EmbedCodeSectionKey): 'html-mixed' | 'css' | 'js' => {
  if (tabKey === 'cssLess') {
    return 'css';
  }

  if (tabKey === 'js') {
    return 'js';
  }

  return 'html-mixed';
};

type BlockContentPopupState = {
  isOpen: boolean;
  title: string;
  content: string;
  isLoading: boolean;
  error: string | null;
};

const createEmptyBlockContentPopupState = (): BlockContentPopupState => ({
  isOpen: false,
  title: '',
  content: '',
  isLoading: false,
  error: null
});

const stringifyBlockContentValue = (value: unknown): string => {
  if (typeof value === 'string') {
    return value;
  }

  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

const copyTextToClipboard = async (text: string): Promise<boolean> => {
  try {
    if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
      await navigator.clipboard.writeText(text);
      return true;
    }

    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'absolute';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();
    const isCopied = document.execCommand('copy');
    document.body.removeChild(textarea);
    return isCopied;
  } catch (error) {
    console.error('Failed to copy text to clipboard', error);
    return false;
  }
};

type Props = {
  isMobile?: boolean;
  onClose?: () => void;
  lastToolsCalls?: completionFunctionalToolsCallsLogType[];
  totalTokens?: number;
  allowedTools?: string[];
  onAllowedToolsChange?: (tools: string[]) => void;
  sessionCurrentContext?: SessionContextDisplay | null;
  sessionContextHistory?: SessionContextDisplay[];
  sessionSummary?: SessionSummary | null;
  onSummaryUpdate?: (summary: SessionSummary | null) => void;
  sessionRenameOptions?: SessionRenameOptions | null;
  onRenameOptionsUpdate?: (renameOptions: SessionRenameOptions | null) => void;
  sessionUsage?: SessionUsageInfo | null;
};

export default function BriefSidebar({
  isMobile = false,
  onClose,
  lastToolsCalls = [],
  totalTokens,
  allowedTools = [],
  onAllowedToolsChange,
  sessionCurrentContext = null,
  sessionContextHistory = [],
  sessionSummary = null,
  onSummaryUpdate,
  sessionRenameOptions = null,
  onRenameOptionsUpdate,
  sessionUsage = null
}: Props) {
  const [availableTools, setAvailableTools] = useState<AvailableToolsListType[]>([]);
  const [selectedTools, setSelectedTools] = useState<Set<string>>(new Set(allowedTools));
  const [availableToolsFilter, setAvailableToolsFilter] = useState('');
  const availableToolsFilterInputRef = useRef<HTMLInputElement>(null);
  const { selectedSession, setSelectedSession } = useSession();
  const [websiteId, setWebsiteId] = useState('');
  const [pageName, setPageName] = useState<PageNameOption | ''>('');
  const [contextSubmitting, setContextSubmitting] = useState(false);
  const [contextFeedback, setContextFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [summarySubmitting, setSummarySubmitting] = useState(false);
  const [summaryFeedback, setSummaryFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [renameSubmitting, setRenameSubmitting] = useState(false);
  const [renameFeedback, setRenameFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [renamedSessionName, setRenamedSessionName] = useState<string | null>(null);
  const [activeCustomCodeTab, setActiveCustomCodeTab] = useState<CustomCodeSectionKey>('head');
  const [customCodeWebsites, setCustomCodeWebsites] = useState<WebliumWebsiteMentionItem[]>([]);
  const [selectedCustomCodeWebsiteId, setSelectedCustomCodeWebsiteId] = useState<string>('');
  const [customCodeWebsitesLoading, setCustomCodeWebsitesLoading] = useState(false);
  const [customCodeWebsitesError, setCustomCodeWebsitesError] = useState<string | null>(null);
  const [customCodeValues, setCustomCodeValues] = useState<CustomCodeSectionsType>(createEmptyCustomCodeSections());
  const [savedCustomCodeValues, setSavedCustomCodeValues] = useState<CustomCodeSectionsType>(createEmptyCustomCodeSections());
  const [customCodeLoading, setCustomCodeLoading] = useState(false);
  const [customCodeSaving, setCustomCodeSaving] = useState(false);
  const [customCodeFeedback, setCustomCodeFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [activeEmbedCodeTab, setActiveEmbedCodeTab] = useState<EmbedCodeSectionKey>('html');
  const [selectedEmbedCodeWebsiteId, setSelectedEmbedCodeWebsiteId] = useState<string>('');
  const [embedCodePages, setEmbedCodePages] = useState<WebliumWebsitePageMentionItem[]>([]);
  const [selectedEmbedCodePageId, setSelectedEmbedCodePageId] = useState<string>('');
  const [embedCodeBlocks, setEmbedCodeBlocks] = useState<WebliumWebsiteBlockMentionItem[]>([]);
  const [selectedEmbedCodeBlockId, setSelectedEmbedCodeBlockId] = useState<string>('');
  const [embedCodePagesLoading, setEmbedCodePagesLoading] = useState(false);
  const [embedCodePagesError, setEmbedCodePagesError] = useState<string | null>(null);
  const [embedCodeBlocksLoading, setEmbedCodeBlocksLoading] = useState(false);
  const [embedCodeBlocksError, setEmbedCodeBlocksError] = useState<string | null>(null);
  const [embedCodeValues, setEmbedCodeValues] = useState<EmbedCodeSectionsType>(createEmptyEmbedCodeSections());
  const [savedEmbedCodeValues, setSavedEmbedCodeValues] = useState<EmbedCodeSectionsType>(createEmptyEmbedCodeSections());
  const [embedCodeIsolateStyles, setEmbedCodeIsolateStyles] = useState<boolean>(false);
  const [savedEmbedCodeIsolateStyles, setSavedEmbedCodeIsolateStyles] = useState<boolean>(false);
  const [embedCodeLoading, setEmbedCodeLoading] = useState(false);
  const [embedCodeSaving, setEmbedCodeSaving] = useState(false);
  const [embedCodeFeedback, setEmbedCodeFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [selectedBlockContentWebsiteId, setSelectedBlockContentWebsiteId] = useState<string>('');
  const [blockContentPages, setBlockContentPages] = useState<WebliumWebsitePageMentionItem[]>([]);
  const [selectedBlockContentPageId, setSelectedBlockContentPageId] = useState<string>('');
  const [blockContentBlocks, setBlockContentBlocks] = useState<WebliumWebsiteBlockMentionItem[]>([]);
  const [selectedBlockContentBlockId, setSelectedBlockContentBlockId] = useState<string>('');
  const [blockContentPagesLoading, setBlockContentPagesLoading] = useState(false);
  const [blockContentPagesError, setBlockContentPagesError] = useState<string | null>(null);
  const [blockContentBlocksLoading, setBlockContentBlocksLoading] = useState(false);
  const [blockContentBlocksError, setBlockContentBlocksError] = useState<string | null>(null);
  const [blockContentSummaryLoading, setBlockContentSummaryLoading] = useState(false);
  const [blockContentSummary, setBlockContentSummary] = useState<AdminBlockContentSummaryType | null>(null);
  const [blockContentFeedback, setBlockContentFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [blockContentPopupState, setBlockContentPopupState] = useState<BlockContentPopupState>(createEmptyBlockContentPopupState);
  const [isBlockContentPopupCopied, setIsBlockContentPopupCopied] = useState(false);
  const [selectedPushWebsiteId, setSelectedPushWebsiteId] = useState('');
  const [pushPages, setPushPages] = useState<WebliumWebsitePageMentionItem[]>([]);
  const [selectedPushPageId, setSelectedPushPageId] = useState('');
  const [pushBlocks, setPushBlocks] = useState<WebliumWebsiteBlockMentionItem[]>([]);
  const [selectedPushBlockId, setSelectedPushBlockId] = useState('');
  const [pushPagesLoading, setPushPagesLoading] = useState(false);
  const [pushPagesError, setPushPagesError] = useState<string | null>(null);
  const [pushBlocksLoading, setPushBlocksLoading] = useState(false);
  const [pushBlocksError, setPushBlocksError] = useState<string | null>(null);
  const [blockPushSubmitting, setBlockPushSubmitting] = useState(false);
  const [blockPushFeedback, setBlockPushFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [expandedSidebarSections, setExpandedSidebarSections] = useState<Set<CollapsibleSidebarSectionKey>>(createDefaultExpandedSidebarSections);
  const [expandedToolCalls, setExpandedToolCalls] = useState<Set<string>>(new Set());
  const [copiedToolResultKey, setCopiedToolResultKey] = useState<string | null>(null);
  const [copiedSessionInfoIdKey, setCopiedSessionInfoIdKey] = useState<string | null>(null);
  const [systemConnectionTestRunning, setSystemConnectionTestRunning] = useState(false);
  const [systemConnectionTestStartedAtMs, setSystemConnectionTestStartedAtMs] = useState<number | null>(null);
  const [systemConnectionTestElapsedSeconds, setSystemConnectionTestElapsedSeconds] = useState(0);
  const [systemConnectionTestResult, setSystemConnectionTestResult] = useState<SystemConnectionHoldTestResponse | null>(null);
  const [systemConnectionTestError, setSystemConnectionTestError] = useState<string | null>(null);
  const [systemConfigLoading, setSystemConfigLoading] = useState(false);
  const [systemConfigResult, setSystemConfigResult] = useState<SystemEnvConfigResponse | null>(null);
  const [systemConfigError, setSystemConfigError] = useState<string | null>(null);
  const [systemWebliumAuthLoading, setSystemWebliumAuthLoading] = useState(false);
  const [systemWebliumAuthResult, setSystemWebliumAuthResult] = useState<SystemWebliumAuthCheckResponse | null>(null);
  const [systemWebliumAuthError, setSystemWebliumAuthError] = useState<string | null>(null);

  useEffect(() => {
    const token = localStorage.getItem('authToken') || '';
    if (!token) return;
    fetchAvailableTools(token).then(setAvailableTools).catch(() => setAvailableTools([]));
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('authToken') || '';
    if (!token) {
      setCustomCodeWebsites([]);
      setCustomCodeWebsitesError('Authorization token is missing.');
      return;
    }

    let isCancelled = false;
    setCustomCodeWebsitesLoading(true);
    setCustomCodeWebsitesError(null);

    fetchWebliumWebsites(token)
      .then((websites) => {
        if (isCancelled) {
          return;
        }
        setCustomCodeWebsites(websites);
      })
      .catch((error) => {
        if (isCancelled) {
          return;
        }
        setCustomCodeWebsites([]);
        setCustomCodeWebsitesError(error instanceof Error ? error.message : 'Failed to load websites.');
      })
      .finally(() => {
        if (!isCancelled) {
          setCustomCodeWebsitesLoading(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, []);

  // Keep internal selection in sync with parent-provided allowedTools
  useEffect(() => {
    setSelectedTools(new Set(allowedTools));
  }, [allowedTools]);

  useEffect(() => {
    setWebsiteId('');
    setPageName('');
    setContextFeedback(null);
    setSummaryFeedback(null);
    setRenameFeedback(null);
    setRenamedSessionName(null);
    setSelectedCustomCodeWebsiteId('');
    setCustomCodeFeedback(null);
    setActiveCustomCodeTab('head');
    setSelectedEmbedCodeWebsiteId('');
    setEmbedCodePages([]);
    setSelectedEmbedCodePageId('');
    setEmbedCodeBlocks([]);
    setSelectedEmbedCodeBlockId('');
    setEmbedCodePagesError(null);
    setEmbedCodeBlocksError(null);
    setEmbedCodeFeedback(null);
    setEmbedCodeIsolateStyles(false);
    setSavedEmbedCodeIsolateStyles(false);
    setActiveEmbedCodeTab('html');
    setSelectedBlockContentWebsiteId('');
    setBlockContentPages([]);
    setSelectedBlockContentPageId('');
    setBlockContentBlocks([]);
    setSelectedBlockContentBlockId('');
    setBlockContentPagesError(null);
    setBlockContentBlocksError(null);
    setBlockContentSummary(null);
    setBlockContentFeedback(null);
    setBlockContentPopupState(createEmptyBlockContentPopupState());
    setIsBlockContentPopupCopied(false);
    setSelectedPushWebsiteId('');
    setPushPages([]);
    setSelectedPushPageId('');
    setPushBlocks([]);
    setSelectedPushBlockId('');
    setPushPagesError(null);
    setPushBlocksError(null);
    setBlockPushSubmitting(false);
    setBlockPushFeedback(null);
    setExpandedSidebarSections(createDefaultExpandedSidebarSections());
    setExpandedToolCalls(new Set());
    setCopiedToolResultKey(null);
    setCopiedSessionInfoIdKey(null);
  }, [selectedSession?.id]);

  useEffect(() => {
    if (!copiedSessionInfoIdKey) {
      return;
    }

    const timeout = window.setTimeout(() => {
      setCopiedSessionInfoIdKey(null);
    }, 1800);

    return () => {
      window.clearTimeout(timeout);
    };
  }, [copiedSessionInfoIdKey]);

  useEffect(() => {
    if (!copiedToolResultKey) {
      return;
    }

    const timeout = window.setTimeout(() => {
      setCopiedToolResultKey(null);
    }, 1800);

    return () => {
      window.clearTimeout(timeout);
    };
  }, [copiedToolResultKey]);

  useEffect(() => {
    if (!isBlockContentPopupCopied) {
      return;
    }

    const timeout = window.setTimeout(() => {
      setIsBlockContentPopupCopied(false);
    }, 1800);

    return () => {
      window.clearTimeout(timeout);
    };
  }, [isBlockContentPopupCopied]);

  useEffect(() => {
    if (!systemConnectionTestRunning || systemConnectionTestStartedAtMs === null) {
      return;
    }

    const updateElapsedSeconds = () => {
      setSystemConnectionTestElapsedSeconds(Math.floor((Date.now() - systemConnectionTestStartedAtMs) / 1000));
    };

    updateElapsedSeconds();
    const interval = window.setInterval(updateElapsedSeconds, 1000);

    return () => {
      window.clearInterval(interval);
    };
  }, [systemConnectionTestRunning, systemConnectionTestStartedAtMs]);

  useEffect(() => {
    if (!blockContentPopupState.isOpen) {
      return;
    }

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setBlockContentPopupState(createEmptyBlockContentPopupState());
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => {
      window.removeEventListener('keydown', handleEscape);
    };
  }, [blockContentPopupState.isOpen]);

  // When tools are fetched first time and parent has no selection, default to all
  useEffect(() => {
    if (availableTools.length > 0 && allowedTools.length === 0) {
      const all = availableTools.map(t => t.name);
      setSelectedTools(new Set(all));
      onAllowedToolsChange && onAllowedToolsChange(all);
    }
  }, [availableTools, allowedTools.length, onAllowedToolsChange]);

  useEffect(() => {
    const selectedWebsiteId = selectedCustomCodeWebsiteId;

    if (!selectedWebsiteId) {
      const emptyValues = createEmptyCustomCodeSections();
      setCustomCodeValues(emptyValues);
      setSavedCustomCodeValues(emptyValues);
      setCustomCodeLoading(false);
      setCustomCodeFeedback(null);
      return;
    }

    const token = localStorage.getItem('authToken') || '';
    if (!token) {
      const emptyValues = createEmptyCustomCodeSections();
      setCustomCodeValues(emptyValues);
      setSavedCustomCodeValues(emptyValues);
      setCustomCodeLoading(false);
      setCustomCodeFeedback({ type: 'error', message: 'Authorization token is missing.' });
      return;
    }

    let isCancelled = false;
    setCustomCodeLoading(true);
    setCustomCodeFeedback(null);

    fetchAdminCustomCodeByWebsiteId(token, selectedWebsiteId)
      .then((sections) => {
        if (isCancelled) {
          return;
        }
        setCustomCodeValues(sections);
        setSavedCustomCodeValues(sections);
      })
      .catch((error) => {
        if (isCancelled) {
          return;
        }
        const emptyValues = createEmptyCustomCodeSections();
        setCustomCodeValues(emptyValues);
        setSavedCustomCodeValues(emptyValues);
        setCustomCodeFeedback({
          type: 'error',
          message: error instanceof Error ? error.message : 'Failed to load custom code.'
        });
      })
      .finally(() => {
        if (!isCancelled) {
          setCustomCodeLoading(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [selectedCustomCodeWebsiteId]);

  useEffect(() => {
    const emptyValues = createEmptyEmbedCodeSections();

    setEmbedCodePages([]);
    setSelectedEmbedCodePageId('');
    setEmbedCodeBlocks([]);
    setSelectedEmbedCodeBlockId('');
    setEmbedCodePagesError(null);
    setEmbedCodeBlocksError(null);
    setEmbedCodeFeedback(null);
    setEmbedCodeValues(emptyValues);
    setSavedEmbedCodeValues(emptyValues);
    setEmbedCodeIsolateStyles(false);
    setSavedEmbedCodeIsolateStyles(false);
    setEmbedCodeLoading(false);

    const selectedWebsiteId = selectedEmbedCodeWebsiteId;
    if (!selectedWebsiteId) {
      setEmbedCodePagesLoading(false);
      return;
    }

    const token = localStorage.getItem('authToken') || '';
    if (!token) {
      setEmbedCodePagesLoading(false);
      setEmbedCodePagesError('Authorization token is missing.');
      return;
    }

    let isCancelled = false;
    setEmbedCodePagesLoading(true);
    setEmbedCodePagesError(null);

    fetchWebliumWebsitePages(token, selectedWebsiteId)
      .then((pages) => {
        if (isCancelled) {
          return;
        }
        setEmbedCodePages(pages);
      })
      .catch((error) => {
        if (isCancelled) {
          return;
        }
        setEmbedCodePages([]);
        setEmbedCodePagesError(error instanceof Error ? error.message : 'Failed to load pages.');
      })
      .finally(() => {
        if (!isCancelled) {
          setEmbedCodePagesLoading(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [selectedEmbedCodeWebsiteId]);

  useEffect(() => {
    const emptyValues = createEmptyEmbedCodeSections();
    const selectedWebsiteId = selectedEmbedCodeWebsiteId;
    const selectedPageId = selectedEmbedCodePageId;

    setEmbedCodeBlocks([]);
    setSelectedEmbedCodeBlockId('');
    setEmbedCodeBlocksError(null);
    setEmbedCodeFeedback(null);
    setEmbedCodeValues(emptyValues);
    setSavedEmbedCodeValues(emptyValues);
    setEmbedCodeIsolateStyles(false);
    setSavedEmbedCodeIsolateStyles(false);
    setEmbedCodeLoading(false);

    if (!selectedWebsiteId || !selectedPageId) {
      setEmbedCodeBlocksLoading(false);
      return;
    }

    const token = localStorage.getItem('authToken') || '';
    if (!token) {
      setEmbedCodeBlocksLoading(false);
      setEmbedCodeBlocksError('Authorization token is missing.');
      return;
    }

    let isCancelled = false;
    setEmbedCodeBlocksLoading(true);
    setEmbedCodeBlocksError(null);

    fetchWebliumWebsiteBlocks(token, selectedWebsiteId, selectedPageId)
      .then((blocks) => {
        if (isCancelled) {
          return;
        }

        const embedOnlyBlocks = blocks.filter(
          (block) => Array.isArray(block.roles) && block.roles.includes('embed')
        );

        if (embedOnlyBlocks.length === 0) {
          setEmbedCodeBlocks([]);
          setSelectedEmbedCodeBlockId(NO_EMBED_BLOCK_OPTION_ID);
          return;
        }

        setEmbedCodeBlocks(embedOnlyBlocks);
        setSelectedEmbedCodeBlockId('');
      })
      .catch((error) => {
        if (isCancelled) {
          return;
        }
        setEmbedCodeBlocks([]);
        setSelectedEmbedCodeBlockId('');
        setEmbedCodeBlocksError(error instanceof Error ? error.message : 'Failed to load blocks.');
      })
      .finally(() => {
        if (!isCancelled) {
          setEmbedCodeBlocksLoading(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [selectedEmbedCodeWebsiteId, selectedEmbedCodePageId]);

  useEffect(() => {
    const hasValidSelection = Boolean(
      selectedEmbedCodeWebsiteId
      && selectedEmbedCodePageId
      && selectedEmbedCodeBlockId
      && selectedEmbedCodeBlockId !== NO_EMBED_BLOCK_OPTION_ID
    );

    if (!hasValidSelection) {
      const emptyValues = createEmptyEmbedCodeSections();
      setEmbedCodeValues(emptyValues);
      setSavedEmbedCodeValues(emptyValues);
      setEmbedCodeIsolateStyles(false);
      setSavedEmbedCodeIsolateStyles(false);
      setEmbedCodeLoading(false);
      return;
    }

    const token = localStorage.getItem('authToken') || '';
    if (!token) {
      const emptyValues = createEmptyEmbedCodeSections();
      setEmbedCodeValues(emptyValues);
      setSavedEmbedCodeValues(emptyValues);
      setEmbedCodeIsolateStyles(false);
      setSavedEmbedCodeIsolateStyles(false);
      setEmbedCodeLoading(false);
      setEmbedCodeFeedback({ type: 'error', message: 'Authorization token is missing.' });
      return;
    }

    let isCancelled = false;
    setEmbedCodeLoading(true);
    setEmbedCodeFeedback(null);

    fetchAdminEmbedCodeByBlockId(
      token,
      selectedEmbedCodeWebsiteId,
      selectedEmbedCodePageId,
      selectedEmbedCodeBlockId
    )
      .then((embedCodeData) => {
        if (isCancelled) {
          return;
        }
        setEmbedCodeValues(embedCodeData.sections);
        setSavedEmbedCodeValues(embedCodeData.sections);
        setEmbedCodeIsolateStyles(embedCodeData.isolateStyles);
        setSavedEmbedCodeIsolateStyles(embedCodeData.isolateStyles);
      })
      .catch((error) => {
        if (isCancelled) {
          return;
        }
        const emptyValues = createEmptyEmbedCodeSections();
        setEmbedCodeValues(emptyValues);
        setSavedEmbedCodeValues(emptyValues);
        setEmbedCodeIsolateStyles(false);
        setSavedEmbedCodeIsolateStyles(false);
        setEmbedCodeFeedback({
          type: 'error',
          message: error instanceof Error ? error.message : 'Failed to load embed code.'
        });
      })
      .finally(() => {
        if (!isCancelled) {
          setEmbedCodeLoading(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [selectedEmbedCodeWebsiteId, selectedEmbedCodePageId, selectedEmbedCodeBlockId]);

  useEffect(() => {
    setBlockContentPages([]);
    setSelectedBlockContentPageId('');
    setBlockContentBlocks([]);
    setSelectedBlockContentBlockId('');
    setBlockContentPagesError(null);
    setBlockContentBlocksError(null);
    setBlockContentSummary(null);
    setBlockContentFeedback(null);
    setBlockContentSummaryLoading(false);

    const selectedWebsiteId = selectedBlockContentWebsiteId;
    if (!selectedWebsiteId) {
      setBlockContentPagesLoading(false);
      return;
    }

    const token = localStorage.getItem('authToken') || '';
    if (!token) {
      setBlockContentPagesLoading(false);
      setBlockContentPagesError('Authorization token is missing.');
      return;
    }

    let isCancelled = false;
    setBlockContentPagesLoading(true);

    fetchWebliumWebsitePages(token, selectedWebsiteId)
      .then((pages) => {
        if (isCancelled) {
          return;
        }
        setBlockContentPages(pages);
      })
      .catch((error) => {
        if (isCancelled) {
          return;
        }
        setBlockContentPages([]);
        setBlockContentPagesError(error instanceof Error ? error.message : 'Failed to load pages.');
      })
      .finally(() => {
        if (!isCancelled) {
          setBlockContentPagesLoading(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [selectedBlockContentWebsiteId]);

  useEffect(() => {
    setBlockContentBlocks([]);
    setSelectedBlockContentBlockId('');
    setBlockContentBlocksError(null);
    setBlockContentSummary(null);
    setBlockContentFeedback(null);
    setBlockContentSummaryLoading(false);

    const selectedWebsiteId = selectedBlockContentWebsiteId;
    const selectedPageId = selectedBlockContentPageId;

    if (!selectedWebsiteId || !selectedPageId) {
      setBlockContentBlocksLoading(false);
      return;
    }

    const token = localStorage.getItem('authToken') || '';
    if (!token) {
      setBlockContentBlocksLoading(false);
      setBlockContentBlocksError('Authorization token is missing.');
      return;
    }

    let isCancelled = false;
    setBlockContentBlocksLoading(true);

    fetchWebliumWebsiteBlocks(token, selectedWebsiteId, selectedPageId)
      .then((blocks) => {
        if (isCancelled) {
          return;
        }

        if (blocks.length === 0) {
          setBlockContentBlocks([]);
          setSelectedBlockContentBlockId(NO_BLOCK_CONTENT_BLOCK_OPTION_ID);
          return;
        }

        setBlockContentBlocks(blocks);
        setSelectedBlockContentBlockId('');
      })
      .catch((error) => {
        if (isCancelled) {
          return;
        }
        setBlockContentBlocks([]);
        setSelectedBlockContentBlockId('');
        setBlockContentBlocksError(error instanceof Error ? error.message : 'Failed to load blocks.');
      })
      .finally(() => {
        if (!isCancelled) {
          setBlockContentBlocksLoading(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [selectedBlockContentWebsiteId, selectedBlockContentPageId]);

  useEffect(() => {
    const hasValidSelection = Boolean(
      selectedBlockContentWebsiteId
      && selectedBlockContentPageId
      && selectedBlockContentBlockId
      && selectedBlockContentBlockId !== NO_BLOCK_CONTENT_BLOCK_OPTION_ID
    );

    if (!hasValidSelection) {
      setBlockContentSummary(null);
      setBlockContentSummaryLoading(false);
      return;
    }

    const token = localStorage.getItem('authToken') || '';
    if (!token) {
      setBlockContentSummary(null);
      setBlockContentSummaryLoading(false);
      setBlockContentFeedback({ type: 'error', message: 'Authorization token is missing.' });
      return;
    }

    let isCancelled = false;
    setBlockContentSummaryLoading(true);
    setBlockContentFeedback(null);

    fetchAdminBlockContentSummaryByBlockId(
      token,
      selectedBlockContentWebsiteId,
      selectedBlockContentPageId,
      selectedBlockContentBlockId
    )
      .then((summary) => {
        if (isCancelled) {
          return;
        }
        setBlockContentSummary(summary);
      })
      .catch((error) => {
        if (isCancelled) {
          return;
        }
        setBlockContentSummary(null);
        setBlockContentFeedback({
          type: 'error',
          message: error instanceof Error ? error.message : 'Failed to load block content summary.'
        });
      })
      .finally(() => {
        if (!isCancelled) {
          setBlockContentSummaryLoading(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [selectedBlockContentWebsiteId, selectedBlockContentPageId, selectedBlockContentBlockId]);

  useEffect(() => {
    setPushPages([]);
    setSelectedPushPageId('');
    setPushBlocks([]);
    setSelectedPushBlockId('');
    setPushPagesError(null);
    setPushBlocksError(null);
    setBlockPushFeedback(null);

    if (!selectedPushWebsiteId) {
      setPushPagesLoading(false);
      return;
    }

    const token = localStorage.getItem('authToken') || '';
    if (!token) {
      setPushPagesLoading(false);
      setPushPagesError('Authorization token is missing.');
      return;
    }

    let isCancelled = false;
    setPushPagesLoading(true);

    fetchWebliumWebsitePages(token, selectedPushWebsiteId)
      .then((pages) => {
        if (!isCancelled) {
          setPushPages(pages);
        }
      })
      .catch((error) => {
        if (!isCancelled) {
          setPushPages([]);
          setPushPagesError(error instanceof Error ? error.message : 'Failed to load pages.');
        }
      })
      .finally(() => {
        if (!isCancelled) {
          setPushPagesLoading(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [selectedPushWebsiteId]);

  useEffect(() => {
    setPushBlocks([]);
    setSelectedPushBlockId('');
    setPushBlocksError(null);
    setBlockPushFeedback(null);

    if (!selectedPushWebsiteId || !selectedPushPageId) {
      setPushBlocksLoading(false);
      return;
    }

    const token = localStorage.getItem('authToken') || '';
    if (!token) {
      setPushBlocksLoading(false);
      setPushBlocksError('Authorization token is missing.');
      return;
    }

    let isCancelled = false;
    setPushBlocksLoading(true);

    fetchWebliumWebsiteBlocks(token, selectedPushWebsiteId, selectedPushPageId)
      .then((blocks) => {
        if (isCancelled) {
          return;
        }

        setPushBlocks(blocks);
        setSelectedPushBlockId(blocks.length === 0 ? NO_PUSH_BLOCK_OPTION_ID : '');
      })
      .catch((error) => {
        if (!isCancelled) {
          setPushBlocks([]);
          setSelectedPushBlockId('');
          setPushBlocksError(error instanceof Error ? error.message : 'Failed to load blocks.');
        }
      })
      .finally(() => {
        if (!isCancelled) {
          setPushBlocksLoading(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [selectedPushWebsiteId, selectedPushPageId]);

  const toggleTool = (toolName: string) => {
    const next = new Set(selectedTools);
    if (next.has(toolName)) {
      next.delete(toolName);
    } else {
      next.add(toolName);
    }
    setSelectedTools(next);
    onAllowedToolsChange && onAllowedToolsChange(Array.from(next));
  };

  const toggleToolCallExpansion = (callKey: string) => {
    setExpandedToolCalls((prev) => {
      const next = new Set(prev);
      if (next.has(callKey)) {
        next.delete(callKey);
      } else {
        next.add(callKey);
      }
      return next;
    });
  };

  const toggleSidebarSectionExpansion = (sectionKey: CollapsibleSidebarSectionKey) => {
    if (sectionKey === 'availableTools' && expandedSidebarSections.has(sectionKey)) {
      setAvailableToolsFilter('');
    }

    setExpandedSidebarSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionKey)) {
        next.delete(sectionKey);
      } else {
        next.add(sectionKey);
      }
      return next;
    });
  };

  const handleCopyToolResult = async (result: string, callKey: string) => {
    if (!result) {
      return;
    }

    const copied = await copyTextToClipboard(result);
    if (copied) {
      setCopiedToolResultKey(callKey);
    }
  };

  const displayTokens = totalTokens;
  const selectedCustomCodeWebsite = customCodeWebsites.find((website) => website.id === selectedCustomCodeWebsiteId) || null;
  const selectedEmbedCodeWebsite = customCodeWebsites.find((website) => website.id === selectedEmbedCodeWebsiteId) || null;
  const selectedEmbedCodePage = embedCodePages.find((page) => page.id === selectedEmbedCodePageId) || null;
  const selectedEmbedCodeBlock = embedCodeBlocks.find((block) => block.id === selectedEmbedCodeBlockId) || null;
  const selectedBlockContentWebsite = customCodeWebsites.find((website) => website.id === selectedBlockContentWebsiteId) || null;
  const selectedBlockContentPage = blockContentPages.find((page) => page.id === selectedBlockContentPageId) || null;
  const selectedBlockContentBlock = blockContentBlocks.find((block) => block.id === selectedBlockContentBlockId) || null;
  const selectedPushWebsite = customCodeWebsites.find((website) => website.id === selectedPushWebsiteId) || null;
  const selectedPushPage = pushPages.find((page) => page.id === selectedPushPageId) || null;
  const selectedPushBlock = pushBlocks.find((block) => block.id === selectedPushBlockId) || null;
  const isContextDisabled = !selectedSession || contextSubmitting;
  const hasInput = websiteId.trim() !== '' || pageName !== '';
  const hasExistingContext = Boolean(sessionCurrentContext?.websiteId || sessionCurrentContext?.pageName);
  const canSubmitContext = Boolean(selectedSession && !contextSubmitting && (hasInput || hasExistingContext));
  const hasSelectedCustomCodeWebsite = Boolean(selectedCustomCodeWebsiteId);
  const hasSelectedEmbedCodeWebsite = Boolean(selectedEmbedCodeWebsiteId);
  const hasSelectedEmbedCodePage = Boolean(selectedEmbedCodePageId);
  const hasSelectedValidEmbedBlock = Boolean(
    selectedEmbedCodeBlockId
    && selectedEmbedCodeBlockId !== NO_EMBED_BLOCK_OPTION_ID
  );
  const hasNoEmbedBlocks = selectedEmbedCodeBlockId === NO_EMBED_BLOCK_OPTION_ID;
  const hasSelectedBlockContentWebsite = Boolean(selectedBlockContentWebsiteId);
  const hasSelectedBlockContentPage = Boolean(selectedBlockContentPageId);
  const hasSelectedValidBlockContentBlock = Boolean(
    selectedBlockContentBlockId
    && selectedBlockContentBlockId !== NO_BLOCK_CONTENT_BLOCK_OPTION_ID
  );
  const hasNoBlockContentBlocks = selectedBlockContentBlockId === NO_BLOCK_CONTENT_BLOCK_OPTION_ID;
  const hasSelectedPushWebsite = Boolean(selectedPushWebsiteId);
  const hasSelectedPushPage = Boolean(selectedPushPageId);
  const hasSelectedValidPushBlock = Boolean(
    selectedPushBlockId
    && selectedPushBlockId !== NO_PUSH_BLOCK_OPTION_ID
  );
  const hasNoPushBlocks = selectedPushBlockId === NO_PUSH_BLOCK_OPTION_ID;
  const canPushBlock = (
    hasSelectedPushWebsite
    && hasSelectedPushPage
    && hasSelectedValidPushBlock
    && !blockPushSubmitting
  );
  const isCustomCodeTabModified = (tabKey: CustomCodeSectionKey): boolean => customCodeValues[tabKey] !== savedCustomCodeValues[tabKey];
  const isCustomCodeTabEmpty = (tabKey: CustomCodeSectionKey): boolean => customCodeValues[tabKey].trim().length === 0;
  const hasCustomCodeChanges = CUSTOM_CODE_TABS.some((tab) => isCustomCodeTabModified(tab.key));
  const canSaveCustomCode = hasSelectedCustomCodeWebsite && !customCodeLoading && !customCodeSaving && hasCustomCodeChanges;
  const isEmbedCodeTabModified = (tabKey: EmbedCodeSectionKey): boolean => embedCodeValues[tabKey] !== savedEmbedCodeValues[tabKey];
  const isEmbedCodeTabEmpty = (tabKey: EmbedCodeSectionKey): boolean => embedCodeValues[tabKey].trim().length === 0;
  const isEmbedCodeScopeModified = embedCodeIsolateStyles !== savedEmbedCodeIsolateStyles;
  const hasEmbedCodeChanges = EMBED_CODE_TABS.some((tab) => isEmbedCodeTabModified(tab.key)) || isEmbedCodeScopeModified;
  const canSaveEmbedCode = (
    hasSelectedEmbedCodeWebsite
    && hasSelectedEmbedCodePage
    && hasSelectedValidEmbedBlock
    && !embedCodeLoading
    && !embedCodeSaving
    && hasEmbedCodeChanges
  );
  const isCustomCodeSectionExpanded = expandedSidebarSections.has('customCode');
  const isSessionInfoSectionExpanded = expandedSidebarSections.has('sessionInfo');
  const isEmbedCodeSectionExpanded = expandedSidebarSections.has('embedCode');
  const isBlockContentInfoSectionExpanded = expandedSidebarSections.has('blockContentInfo');
  const isPushBlockToWebliumSectionExpanded = expandedSidebarSections.has('pushBlockToWeblium');
  const isSessionContextSectionExpanded = expandedSidebarSections.has('sessionContext');
  const isSessionDataSectionExpanded = expandedSidebarSections.has('sessionData');
  const isSessionRenameSectionExpanded = expandedSidebarSections.has('sessionRename');
  const isSummarySectionExpanded = expandedSidebarSections.has('summary');
  const isAvailableToolsSectionExpanded = expandedSidebarSections.has('availableTools');
  const isLastToolCallsSectionExpanded = expandedSidebarSections.has('lastToolCalls');
  const isStatisticsSectionExpanded = expandedSidebarSections.has('statistics');
  const isLimitsSectionExpanded = expandedSidebarSections.has('limits');
  const isSystemDiagnosticsSectionExpanded = expandedSidebarSections.has('systemDiagnostics');
  const highlightedBlockContentPopup = useMemo(
    () => highlightCustomCodeContent(blockContentPopupState.content, 'js'),
    [blockContentPopupState.content]
  );
  const filteredAvailableTools = useMemo(() => {
    const normalizedFilter = availableToolsFilter.toLocaleLowerCase();

    if (!normalizedFilter) {
      return availableTools;
    }

    return availableTools.filter((tool) => (
      `${tool.name} ${tool.description}`.toLocaleLowerCase().includes(normalizedFilter)
    ));
  }, [availableTools, availableToolsFilter]);

  useEffect(() => {
    if (isAvailableToolsSectionExpanded) {
      availableToolsFilterInputRef.current?.focus();
    }
  }, [isAvailableToolsSectionExpanded]);

  const formatContextValue = (value: string | PageNameOption | '' | null | undefined) => {
    if (value == null || (typeof value === 'string' && value.trim() === '')) {
      return '—';
    }
    return value;
  };

  const handleContextSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selectedSession || !canSubmitContext) {
      return;
    }
    const token = localStorage.getItem('authToken') || '';
    if (!token) {
      setContextFeedback({ type: 'error', message: 'Authorization token is missing.' });
      return;
    }

    try {
      setContextSubmitting(true);
      await setSessionContext(token, selectedSession.id, {
        websiteId: websiteId.trim(),
        pageName,
      });
      setContextFeedback({ type: 'success', message: 'Session context saved.' });
    } catch (error) {
      setContextFeedback({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to save session context.',
      });
    } finally {
      setContextSubmitting(false);
    }
  };

  const handleSummarize = async () => {
    if (!selectedSession || !selectedSession.id) {
      setSummaryFeedback({ type: 'error', message: 'Select a session first.' });
      return;
    }
    const token = localStorage.getItem('authToken') || '';
    if (!token) {
      setSummaryFeedback({ type: 'error', message: 'Authorization token is missing.' });
      return;
    }
    try {
      setSummarySubmitting(true);
      setSummaryFeedback(null);
      const summary = await summarizeSession(token, selectedSession.id);
      onSummaryUpdate && onSummaryUpdate(summary);
      setSummaryFeedback({ type: 'success', message: 'Summary updated.' });
    } catch (error) {
      setSummaryFeedback({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to summarize session.'
      });
    } finally {
      setSummarySubmitting(false);
    }
  };

  const formatSummaryTimestamp = (timestamp?: string) => {
    if (!timestamp) {
      return '—';
    }
    const date = new Date(timestamp);
    return isNaN(date.getTime()) ? '—' : date.toLocaleString();
  };

  const handleCustomCodeValueChange = (nextValue: string) => {
    setCustomCodeValues((prev) => ({
      ...prev,
      [activeCustomCodeTab]: nextValue
    }));
    setCustomCodeFeedback(null);
  };

  const handleEmbedCodeValueChange = (nextValue: string) => {
    setEmbedCodeValues((prev) => ({
      ...prev,
      [activeEmbedCodeTab]: nextValue
    }));
    setEmbedCodeFeedback(null);
  };

  const handleSaveCustomCode = async () => {
    if (!selectedCustomCodeWebsiteId) {
      setCustomCodeFeedback({ type: 'error', message: 'Select a website for custom code first.' });
      return;
    }

    const token = localStorage.getItem('authToken') || '';
    if (!token) {
      setCustomCodeFeedback({ type: 'error', message: 'Authorization token is missing.' });
      return;
    }

    try {
      setCustomCodeSaving(true);
      setCustomCodeFeedback(null);
      const savedSections = await saveAdminCustomCodeByWebsiteId(token, selectedCustomCodeWebsiteId, customCodeValues);
      setCustomCodeValues(savedSections);
      setSavedCustomCodeValues(savedSections);
      setCustomCodeFeedback({ type: 'success', message: 'Custom code saved.' });
    } catch (error) {
      setCustomCodeFeedback({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to save custom code.'
      });
    } finally {
      setCustomCodeSaving(false);
    }
  };

  const handleSaveEmbedCode = async () => {
    if (!selectedEmbedCodeWebsiteId || !selectedEmbedCodePageId || !selectedEmbedCodeBlockId) {
      setEmbedCodeFeedback({ type: 'error', message: 'Select website, page, and block for embed code first.' });
      return;
    }

    if (selectedEmbedCodeBlockId === NO_EMBED_BLOCK_OPTION_ID) {
      setEmbedCodeFeedback({ type: 'error', message: 'Selected page does not have embed blocks.' });
      return;
    }

    const token = localStorage.getItem('authToken') || '';
    if (!token) {
      setEmbedCodeFeedback({ type: 'error', message: 'Authorization token is missing.' });
      return;
    }

    try {
      setEmbedCodeSaving(true);
      setEmbedCodeFeedback(null);
      const embedCodePayload: EmbedCodeDataType = {
        sections: embedCodeValues,
        isolateStyles: embedCodeIsolateStyles
      };
      const savedEmbedCode = await saveAdminEmbedCodeByBlockId(
        token,
        selectedEmbedCodeWebsiteId,
        selectedEmbedCodePageId,
        selectedEmbedCodeBlockId,
        embedCodePayload
      );
      setEmbedCodeValues(savedEmbedCode.sections);
      setSavedEmbedCodeValues(savedEmbedCode.sections);
      setEmbedCodeIsolateStyles(savedEmbedCode.isolateStyles);
      setSavedEmbedCodeIsolateStyles(savedEmbedCode.isolateStyles);
      setEmbedCodeFeedback({ type: 'success', message: 'Embed code saved.' });
    } catch (error) {
      setEmbedCodeFeedback({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to save embed code.'
      });
    } finally {
      setEmbedCodeSaving(false);
    }
  };

  const handleCloseBlockContentPopup = () => {
    setBlockContentPopupState(createEmptyBlockContentPopupState());
    setIsBlockContentPopupCopied(false);
  };

  const handleCopyBlockContentPopup = async () => {
    if (!blockContentPopupState.content || blockContentPopupState.isLoading || blockContentPopupState.error) {
      return;
    }

    const copied = await copyTextToClipboard(blockContentPopupState.content);
    if (copied) {
      setIsBlockContentPopupCopied(true);
    }
  };

  const handleOpenBlockContentPopup = async (viewKey: BlockContentDataViewKey) => {
    if (!selectedBlockContentWebsiteId || !selectedBlockContentPageId || !selectedBlockContentBlockId) {
      setBlockContentFeedback({ type: 'error', message: 'Select website, page, and block first.' });
      return;
    }

    if (selectedBlockContentBlockId === NO_BLOCK_CONTENT_BLOCK_OPTION_ID) {
      setBlockContentFeedback({ type: 'error', message: 'Selected page does not have blocks.' });
      return;
    }

    const token = localStorage.getItem('authToken') || '';
    if (!token) {
      setBlockContentFeedback({ type: 'error', message: 'Authorization token is missing.' });
      return;
    }

    const selectedViewOption = BLOCK_CONTENT_VIEW_BUTTONS.find((view) => view.key === viewKey);
    const popupTitle = selectedViewOption ? selectedViewOption.label : 'Block content';

    setIsBlockContentPopupCopied(false);
    setBlockContentPopupState({
      isOpen: true,
      title: popupTitle,
      content: '',
      isLoading: true,
      error: null
    });

    try {
      let payload: unknown;
      if (viewKey === 'flatElements') {
        payload = await fetchAdminBlockContentFlatElementsByBlockId(
          token,
          selectedBlockContentWebsiteId,
          selectedBlockContentPageId,
          selectedBlockContentBlockId
        );
      } else if (viewKey === 'treeElements') {
        payload = await fetchAdminBlockContentTreeElementsByBlockId(
          token,
          selectedBlockContentWebsiteId,
          selectedBlockContentPageId,
          selectedBlockContentBlockId
        );
      } else if (viewKey === 'properties') {
        payload = await fetchAdminBlockContentPropertiesByBlockId(
          token,
          selectedBlockContentWebsiteId,
          selectedBlockContentPageId,
          selectedBlockContentBlockId
        );
      } else if (viewKey === 'fullContent') {
        payload = await fetchAdminBlockContentFullByBlockId(
          token,
          selectedBlockContentWebsiteId,
          selectedBlockContentPageId,
          selectedBlockContentBlockId
        );
      } else {
        payload = await fetchAdminBlockContentRawByBlockId(
          token,
          selectedBlockContentWebsiteId,
          selectedBlockContentPageId,
          selectedBlockContentBlockId
        );
      }

      setBlockContentPopupState({
        isOpen: true,
        title: popupTitle,
        content: stringifyBlockContentValue(payload),
        isLoading: false,
        error: null
      });
    } catch (error) {
      setBlockContentPopupState({
        isOpen: true,
        title: popupTitle,
        content: '',
        isLoading: false,
        error: error instanceof Error ? error.message : 'Failed to load block content.'
      });
    }
  };

  const handlePushBlockToWeblium = async () => {
    if (!canPushBlock) {
      setBlockPushFeedback({ type: 'error', message: 'Select a website, page, and block.' });
      return;
    }

    const token = localStorage.getItem('authToken') || '';
    if (!token) {
      setBlockPushFeedback({ type: 'error', message: 'Authorization token is missing.' });
      return;
    }

    setBlockPushSubmitting(true);
    setBlockPushFeedback(null);

    try {
      const result = await pushAdminBlockContentToWebliumByBlockId(
        token,
        selectedPushWebsiteId,
        selectedPushPageId,
        selectedPushBlockId
      );
      setBlockPushFeedback({ type: 'success', message: result.message });
    } catch (error) {
      setBlockPushFeedback({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to push block content to Weblium.'
      });
    } finally {
      setBlockPushSubmitting(false);
    }
  };

  const handleRename = async (force: boolean = false) => {
    if (!selectedSession || !selectedSession.id) {
      setRenameFeedback({ type: 'error', message: 'Select a session first.' });
      return;
    }
    const token = localStorage.getItem('authToken') || '';
    if (!token) {
      setRenameFeedback({ type: 'error', message: 'Authorization token is missing.' });
      return;
    }
    try {
      setRenameSubmitting(true);
      setRenameFeedback(null);
      const { renameOptions, sessionName } = await renameSessionByAI(token, selectedSession.id, force);
      onRenameOptionsUpdate && onRenameOptionsUpdate(renameOptions || null);
      setRenamedSessionName(sessionName);
      // Update selected session name in context
      setSelectedSession({ ...selectedSession, name: sessionName });
      setRenameFeedback({ type: 'success', message: force ? 'Session renamed (forced).' : 'Session renamed.' });
    } catch (error) {
      setRenameFeedback({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to rename session.'
      });
    } finally {
      setRenameSubmitting(false);
    }
  };

  const handleRunSystemConnectionTest = async () => {
    const token = localStorage.getItem('authToken') || '';
    if (!token) {
      setSystemConnectionTestError('Authorization token is missing.');
      return;
    }

    const startedAtMs = Date.now();
    setSystemConnectionTestRunning(true);
    setSystemConnectionTestStartedAtMs(startedAtMs);
    setSystemConnectionTestElapsedSeconds(0);
    setSystemConnectionTestResult(null);
    setSystemConnectionTestError(null);

    try {
      const result = await runSystemConnectionHoldTest(token);
      setSystemConnectionTestResult(result);
      setSystemConnectionTestElapsedSeconds(Math.max(
        Math.floor(result.actualDurationMs / 1000),
        Math.floor((Date.now() - startedAtMs) / 1000)
      ));
    } catch (error) {
      setSystemConnectionTestError(error instanceof Error ? error.message : 'Connection hold test failed.');
      setSystemConnectionTestElapsedSeconds(Math.floor((Date.now() - startedAtMs) / 1000));
    } finally {
      setSystemConnectionTestRunning(false);
      setSystemConnectionTestStartedAtMs(null);
    }
  };

  const handleLoadSystemConfig = async () => {
    const token = localStorage.getItem('authToken') || '';
    if (!token) {
      setSystemConfigError('Authorization token is missing.');
      return;
    }

    try {
      setSystemConfigLoading(true);
      setSystemConfigResult(null);
      setSystemConfigError(null);
      const result = await fetchSystemEnvConfig(token);
      setSystemConfigResult(result);
    } catch (error) {
      setSystemConfigError(error instanceof Error ? error.message : 'Failed to load system config.');
    } finally {
      setSystemConfigLoading(false);
    }
  };

  const handleCheckSystemWebliumAuth = async () => {
    const token = localStorage.getItem('authToken') || '';
    if (!token) {
      setSystemWebliumAuthError('Authorization token is missing.');
      return;
    }

    try {
      setSystemWebliumAuthLoading(true);
      setSystemWebliumAuthResult(null);
      setSystemWebliumAuthError(null);
      const result = await checkSystemWebliumAuth(token);
      setSystemWebliumAuthResult(result);
    } catch (error) {
      setSystemWebliumAuthError(error instanceof Error ? error.message : 'Weblium authentication check failed.');
    } finally {
      setSystemWebliumAuthLoading(false);
    }
  };

  const handleCopySessionInfoId = async (id: string, idKey: string) => {
    const copied = await copyTextToClipboard(id);
    if (copied) {
      setCopiedSessionInfoIdKey(idKey);
    }
  };

  const renderSessionInfoIdRow = (label: string, id: string | null | undefined, idKey: string) => (
    <div className="border-t border-[#e7eefc] py-2 first:border-t-0 first:pt-0 last:pb-0">
      <div className="text-[10px] font-semibold uppercase tracking-[0.06em] text-[#6b7d9e]">{label}</div>
      <div className="mt-1 flex items-center gap-2">
        <span className="min-w-0 flex-1 break-all font-mono text-[12px] text-[#27344a]">
          {id || 'Not set'}
        </span>
        {id && (
          <button
            type="button"
            onClick={() => {
              void handleCopySessionInfoId(id, idKey);
            }}
            className="group relative shrink-0 rounded-lg border border-[#d7e3ff] bg-white p-1.5 text-[#4f6ea8] transition-colors hover:border-[#97b9ff] hover:text-[#165BFB]"
            title={copiedSessionInfoIdKey === idKey ? 'Copied' : 'Copy ID'}
            aria-label={`Copy ${label}`}
          >
            {copiedSessionInfoIdKey === idKey ? (
              <svg className="size-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 6 9 17l-5-5" />
              </svg>
            ) : (
              <svg className="size-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
              </svg>
            )}
            <span className="pointer-events-none absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-md bg-[#1f2937] px-2 py-1 text-[10px] font-medium text-white opacity-0 transition-opacity group-hover:opacity-100">
              {copiedSessionInfoIdKey === idKey ? 'Copied' : 'Copy ID'}
            </span>
          </button>
        )}
      </div>
    </div>
  );

  const renderSessionInfoNameRow = (
    label: string,
    name: string | null | undefined,
    externalUrl?: string
  ) => (
    <div className="border-t border-[#e7eefc] py-2 first:border-t-0 first:pt-0 last:pb-0">
      <div className="text-[10px] font-semibold uppercase tracking-[0.06em] text-[#6b7d9e]">{label}</div>
      <div className="mt-1 flex items-center gap-2">
        <div className="min-w-0 flex-1 break-words text-[13px] font-medium text-[#27344a]">{name || 'Not set'}</div>
        {externalUrl && (
          <a
            href={externalUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="group relative shrink-0 rounded-lg border border-[#d7e3ff] bg-white p-1.5 text-[#4f6ea8] transition-colors hover:border-[#97b9ff] hover:text-[#165BFB]"
            title="Open in new tab"
            aria-label={`Open ${label.toLowerCase()} in new tab`}
          >
            <svg className="size-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 3h7v7" />
              <path d="M10 14 21 3" />
              <path d="M21 14v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5" />
            </svg>
            <span className="pointer-events-none absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-md bg-[#1f2937] px-2 py-1 text-[10px] font-medium text-white opacity-0 transition-opacity group-hover:opacity-100">
              Open in new tab
            </span>
          </a>
        )}
      </div>
    </div>
  );

  const webliumPortalUrl = WEBLIUM_PORTAL_URL.replace(/\/+$/, '');
  const getWebsiteEditorUrl = (websiteId: string): string => (
    `${webliumPortalUrl}/editor/website/${encodeURIComponent(websiteId)}/`
  );
  const getPageEditorUrl = (websiteId: string, pageId: string): string => (
    `${webliumPortalUrl}/editor/website/${encodeURIComponent(websiteId)}/${encodeURIComponent(pageId)}`
  );

  const renderCollapsibleSectionHeader = (sectionKey: CollapsibleSidebarSectionKey, title: string) => {
    const isExpanded = expandedSidebarSections.has(sectionKey);

    return (
      <div className={`agent-section-heading flex items-start justify-between gap-3 ${isExpanded ? 'mb-4' : ''}`}>
        <div className="font-bold text-[18px] text-[#202c42]">
          <p className="leading-[22px]">{title}</p>
        </div>
        <button
          type="button"
          onClick={() => toggleSidebarSectionExpansion(sectionKey)}
          aria-expanded={isExpanded}
          aria-controls={isExpanded ? COLLAPSIBLE_SIDEBAR_SECTION_CONTENT_IDS[sectionKey] : undefined}
          aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${title}`}
          className="inline-flex size-8 shrink-0 items-center justify-center rounded-lg border border-[#dbe7ff] bg-[#f8fbff] text-[#165BFB] transition-colors hover:border-[#93b5ff] hover:bg-[#eef4ff]"
        >
          <svg
            className={`size-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="m6 9 6 6 6-6" />
          </svg>
        </button>
      </div>
    );
  };

  return (
    <div className={`agent-details bg-[#f6f8ff] h-full ${!isMobile ? 'rounded-tl-[16px]' : 'mobile-brief-sidebar'} overflow-hidden`}>
      {isMobile && (
        <div className="relative">
          <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto my-2" />
          {onClose && (
            <button onClick={onClose} className="absolute right-4 top-0 p-2 text-gray-500 hover:text-gray-700">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z" fill="currentColor"/>
              </svg>
            </button>
          )}
        </div>
      )}

      <div className="agent-details-divider h-0 w-full border-b border-[#EDF0F5] mt-[15px]"></div>

      <div className={`agent-details-scroll pt-8 px-6 pb-8 overflow-y-auto h-full custom-scrollbar flex flex-col ${!isMobile ? 'pt-14 px-8' : ''}`}>
        {/* Title */}
        <div className="agent-details-heading mb-6">
          <div className="flex items-center gap-3">
            <div className="size-10">
              <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 24 24">
                <g>
                  <g>
                    <path d={svgPaths.p1fade100} fill="#165BFB" />
                    <path d={svgPaths.p230cf780} fill="#165BFB" />
                    <path d={svgPaths.p26295800} fill="#165BFB" />
                  </g>
                </g>
              </svg>
            </div>
            <div className="font-bold text-[#165bfb] text-[40px]">
              <p className={`leading-[36px] ${isMobile ? 'text-[32px] leading-[40px]' : ''}`}>Functional Tools</p>
            </div>
          </div>
        </div>

        {/* Block 1: Session Info */}
        <motion.div className="agent-section bg-white rounded-2xl border-2 border-[#edf0f5] p-5 mb-8 order-1" initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.24 }}>
          {renderCollapsibleSectionHeader('sessionInfo', 'Session Info')}
          {isSessionInfoSectionExpanded && (
            <div id={COLLAPSIBLE_SIDEBAR_SECTION_CONTENT_IDS.sessionInfo} className="flex flex-col gap-3">
              <div className="agent-inset rounded-xl border border-[#dbe7ff] bg-[#f8fbff] px-4 py-3">
                <div className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[#4f6ea8]">type</div>
                <div className="mt-1 flex items-center gap-2">
                  <span className="inline-flex rounded-full bg-[#eef4ff] px-3 py-1 text-[13px] font-bold text-[#165BFB]">
                    {selectedSession?.type || 'N/A'}
                  </span>
                </div>
              </div>

              {selectedSession?.type === 'vibe_block' && (
                <div className="agent-inset rounded-xl border border-[#dbe7ff] bg-[#f8fbff] px-4 py-3">
                  <div className="mb-3 text-[11px] font-semibold uppercase tracking-[0.06em] text-[#4f6ea8]">vibe_block</div>
                  {selectedSession.vibe_block ? (
                    <div className="flex flex-col gap-3">
                      <div className="rounded-lg border border-[#e7eefc] bg-white px-3 py-2">
                        <div className="mb-2 text-[12px] font-bold text-[#165BFB]">Website</div>
                        {renderSessionInfoIdRow('ID', selectedSession.vibe_block.website_id, 'vibe-website')}
                        {renderSessionInfoNameRow(
                          'Name',
                          selectedSession.vibe_block.website_name,
                          getWebsiteEditorUrl(selectedSession.vibe_block.website_id)
                        )}
                      </div>
                      <div className="rounded-lg border border-[#e7eefc] bg-white px-3 py-2">
                        <div className="mb-2 text-[12px] font-bold text-[#165BFB]">Page</div>
                        {renderSessionInfoIdRow('ID', selectedSession.vibe_block.page_id, 'vibe-page')}
                        {renderSessionInfoNameRow(
                          'Name',
                          selectedSession.vibe_block.page_name,
                          getPageEditorUrl(
                            selectedSession.vibe_block.website_id,
                            selectedSession.vibe_block.page_id
                          )
                        )}
                      </div>
                      <div className="rounded-lg border border-[#e7eefc] bg-white px-3 py-2">
                        <div className="mb-2 text-[12px] font-bold text-[#165BFB]">Block</div>
                        {renderSessionInfoIdRow('ID', selectedSession.vibe_block.block_id, 'vibe-block')}
                        {renderSessionInfoNameRow('Name', selectedSession.vibe_block.block_name)}
                      </div>
                    </div>
                  ) : (
                    <div className="text-[12px] text-[#5f6f89]">Vibe block context is not available.</div>
                  )}
                </div>
              )}

              {selectedSession?.type === 'general' && (
                <div className="agent-inset rounded-xl border border-[#dbe7ff] bg-[#f8fbff] px-4 py-3">
                  <div className="mb-3 text-[11px] font-semibold uppercase tracking-[0.06em] text-[#4f6ea8]">current_context</div>
                  <div className="rounded-lg border border-[#e7eefc] bg-white px-3 py-2">
                    {renderSessionInfoIdRow('Website ID', sessionCurrentContext?.websiteId, 'context-website')}
                    {renderSessionInfoNameRow(
                      'Website name',
                      sessionCurrentContext?.websiteName,
                      sessionCurrentContext?.websiteId
                        ? getWebsiteEditorUrl(sessionCurrentContext.websiteId)
                        : undefined
                    )}
                    {renderSessionInfoNameRow('Web portal section', sessionCurrentContext?.pageName)}
                  </div>
                </div>
              )}
            </div>
          )}
        </motion.div>

        {/* Block 2: Website Custom Code */}
        <motion.div className="agent-section bg-white rounded-2xl border-2 border-[#edf0f5] p-5 mb-8 order-2" initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.28 }}>
          {renderCollapsibleSectionHeader('customCode', 'Website custom code')}
          {isCustomCodeSectionExpanded && (
            <div id={COLLAPSIBLE_SIDEBAR_SECTION_CONTENT_IDS.customCode} className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <label className="text-[12px] text-[#7a8496]" htmlFor="custom-code-website">
                Website
              </label>
              <select
                id="custom-code-website"
                value={selectedCustomCodeWebsiteId}
                onChange={(event) => {
                  setSelectedCustomCodeWebsiteId(event.target.value);
                  setCustomCodeFeedback(null);
                }}
                disabled={customCodeWebsitesLoading || customCodeSaving}
                className="w-full border border-[#EDF0F5] h-11 px-4 rounded-lg focus:border-[#165BFB] focus:ring-1 focus:ring-[#165BFB] transition-all outline-none bg-white disabled:bg-[#f6f8ff] disabled:text-[#9CA3AF]"
              >
                <option value="">
                  {customCodeWebsitesLoading ? 'Loading websites...' : 'Select website'}
                </option>
                {customCodeWebsites.map((website) => (
                  <option key={website.id} value={website.id}>
                    {website.name || website.domainLabel}
                  </option>
                ))}
              </select>
              {customCodeWebsitesError && (
                <div className="text-[12px] text-[#dc2626]">{customCodeWebsitesError}</div>
              )}
            </div>

            <div className="rounded-xl border border-[#dbe7ff] bg-[#f8fbff] px-3 py-2">
              <div className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[#4f6ea8]">Selected website</div>
              <div className="mt-1 text-[13px] font-semibold text-[#27344a]">
                {selectedCustomCodeWebsite ? selectedCustomCodeWebsite.name || selectedCustomCodeWebsite.domainLabel : 'Not selected'}
              </div>
              <div className="mt-0.5 break-all text-[12px] text-[#5f6f89]">
                {selectedCustomCodeWebsite ? selectedCustomCodeWebsite.domainLabel : 'Select website in the dropdown.'}
              </div>
            </div>

            {!hasSelectedCustomCodeWebsite && (
              <div className="rounded-lg border border-[#dbe7ff] bg-[#f8fbff] px-3 py-2 text-[12px] text-[#5f6f89]">
                Select a website to load and edit custom code.
              </div>
            )}

            {hasSelectedCustomCodeWebsite && (
              <>
                <div className="flex flex-wrap gap-2">
                  {CUSTOM_CODE_TABS.map((tab) => {
                    const isTabModified = isCustomCodeTabModified(tab.key);
                    const isTabEmpty = isCustomCodeTabEmpty(tab.key);

                    return (
                      <button
                        key={tab.key}
                        type="button"
                        onClick={() => setActiveCustomCodeTab(tab.key)}
                        disabled={customCodeLoading || customCodeSaving}
                        className={`rounded-full border px-3 py-1.5 text-[12px] font-semibold transition-colors ${
                          activeCustomCodeTab === tab.key
                            ? 'border-[#93b5ff] bg-[#eef4ff] text-[#165BFB]'
                            : 'border-[#dbe7ff] bg-white text-[#4b5a73] hover:border-[#b8d1ff]'
                        } ${isTabEmpty ? 'border-dashed' : ''} ${customCodeLoading || customCodeSaving ? 'cursor-not-allowed opacity-60' : ''}`}
                      >
                        <span className="inline-flex items-center gap-1.5">
                          <span>{tab.label}</span>
                          {isTabModified && (
                            <span className="text-[#dc2626]" aria-label="Modified tab">*</span>
                          )}
                          {isTabEmpty && (
                            <span className="rounded-full bg-[#eef2ff] px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.04em] text-[#6b7280]">
                              Empty
                            </span>
                          )}
                        </span>
                      </button>
                    );
                  })}
                </div>

                <CustomCodeEditor
                  value={customCodeValues[activeCustomCodeTab]}
                  onChange={handleCustomCodeValueChange}
                  disabled={customCodeLoading || customCodeSaving}
                  placeholder={customCodeLoading ? 'Loading custom code...' : 'Section is empty'}
                  mode={getCustomCodeEditorMode(activeCustomCodeTab)}
                />

                <button
                  type="button"
                  onClick={handleSaveCustomCode}
                  disabled={!canSaveCustomCode}
                  className={`h-11 rounded-lg font-semibold transition-colors ${
                    canSaveCustomCode
                      ? 'bg-[#165bfb] text-white hover:bg-[#1246d7] active:bg-[#0d38b0]'
                      : 'bg-[#cdd7ff] text-white cursor-not-allowed'
                  }`}
                >
                  {customCodeSaving ? 'Saving...' : 'Save custom code'}
                </button>
              </>
            )}

            {customCodeFeedback && (
              <div className={`text-[12px] ${customCodeFeedback.type === 'success' ? 'text-[#166534]' : 'text-[#DC2626]'}`}>
                {customCodeFeedback.message}
              </div>
            )}
            </div>
          )}
        </motion.div>

        {/* Block 2: Page embed code */}
        <motion.div className="agent-section bg-white rounded-2xl border-2 border-[#edf0f5] p-5 mb-8 order-2" initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.29 }}>
          {renderCollapsibleSectionHeader('embedCode', 'Page embed code')}
          {isEmbedCodeSectionExpanded && (
            <div id={COLLAPSIBLE_SIDEBAR_SECTION_CONTENT_IDS.embedCode} className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <label className="text-[12px] text-[#7a8496]" htmlFor="embed-code-website">
                Website
              </label>
              <select
                id="embed-code-website"
                value={selectedEmbedCodeWebsiteId}
                onChange={(event) => {
                  setSelectedEmbedCodeWebsiteId(event.target.value);
                  setEmbedCodeFeedback(null);
                }}
                disabled={customCodeWebsitesLoading || embedCodeSaving || embedCodeLoading}
                className="w-full border border-[#EDF0F5] h-11 px-4 rounded-lg focus:border-[#165BFB] focus:ring-1 focus:ring-[#165BFB] transition-all outline-none bg-white disabled:bg-[#f6f8ff] disabled:text-[#9CA3AF]"
              >
                <option value="">
                  {customCodeWebsitesLoading ? 'Loading websites...' : 'Select website'}
                </option>
                {customCodeWebsites.map((website) => (
                  <option key={website.id} value={website.id}>
                    {website.name || website.domainLabel}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-[12px] text-[#7a8496]" htmlFor="embed-code-page">
                Page
              </label>
              <select
                id="embed-code-page"
                value={selectedEmbedCodePageId}
                onChange={(event) => {
                  setSelectedEmbedCodePageId(event.target.value);
                  setEmbedCodeFeedback(null);
                }}
                disabled={!hasSelectedEmbedCodeWebsite || embedCodePagesLoading || embedCodeSaving || embedCodeLoading}
                className="w-full border border-[#EDF0F5] h-11 px-4 rounded-lg focus:border-[#165BFB] focus:ring-1 focus:ring-[#165BFB] transition-all outline-none bg-white disabled:bg-[#f6f8ff] disabled:text-[#9CA3AF]"
              >
                <option value="">
                  {!hasSelectedEmbedCodeWebsite
                    ? 'Select website first'
                    : (embedCodePagesLoading ? 'Loading pages...' : 'Select page')}
                </option>
                {embedCodePages.map((page) => (
                  <option key={page.id} value={page.id}>
                    {page.label || page.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-[12px] text-[#7a8496]" htmlFor="embed-code-block">
                Block
              </label>
              <select
                id="embed-code-block"
                value={selectedEmbedCodeBlockId}
                onChange={(event) => {
                  setSelectedEmbedCodeBlockId(event.target.value);
                  setEmbedCodeFeedback(null);
                }}
                disabled={!hasSelectedEmbedCodePage || embedCodeBlocksLoading || embedCodeSaving || embedCodeLoading}
                className="w-full border border-[#EDF0F5] h-11 px-4 rounded-lg focus:border-[#165BFB] focus:ring-1 focus:ring-[#165BFB] transition-all outline-none bg-white disabled:bg-[#f6f8ff] disabled:text-[#9CA3AF]"
              >
                {!hasSelectedEmbedCodePage && (
                  <option value="">Select page first</option>
                )}
                {hasSelectedEmbedCodePage && embedCodeBlocksLoading && (
                  <option value="">Loading blocks...</option>
                )}
                {hasSelectedEmbedCodePage && !embedCodeBlocksLoading && hasNoEmbedBlocks && (
                  <option value={NO_EMBED_BLOCK_OPTION_ID}>no embed blocks</option>
                )}
                {hasSelectedEmbedCodePage && !embedCodeBlocksLoading && !hasNoEmbedBlocks && (
                  <>
                    <option value="">Select block</option>
                    {embedCodeBlocks.map((block) => (
                      <option key={block.id} value={block.id}>
                        {getBlockDisplayName(block)}
                      </option>
                    ))}
                  </>
                )}
              </select>
            </div>

            {customCodeWebsitesError && (
              <div className="text-[12px] text-[#dc2626]">{customCodeWebsitesError}</div>
            )}
            {embedCodePagesError && (
              <div className="text-[12px] text-[#dc2626]">{embedCodePagesError}</div>
            )}
            {embedCodeBlocksError && (
              <div className="text-[12px] text-[#dc2626]">{embedCodeBlocksError}</div>
            )}

            <div className="rounded-xl border border-[#dbe7ff] bg-[#f8fbff] px-3 py-2">
              <div className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[#4f6ea8]">Selected targets</div>
              <div className="mt-1 text-[12px] text-[#27344a]">
                Website: {selectedEmbedCodeWebsite ? selectedEmbedCodeWebsite.name || selectedEmbedCodeWebsite.domainLabel : 'Not selected'}
              </div>
              <div className="mt-0.5 text-[12px] text-[#27344a]">
                Page: {selectedEmbedCodePage ? selectedEmbedCodePage.label || selectedEmbedCodePage.name : 'Not selected'}
              </div>
              <div className="mt-0.5 text-[12px] text-[#27344a]">
                Block: {selectedEmbedCodeBlock ? getBlockDisplayName(selectedEmbedCodeBlock) : (hasNoEmbedBlocks ? 'no embed blocks' : 'Not selected')}
              </div>
            </div>

            {!hasSelectedEmbedCodeWebsite && (
              <div className="rounded-lg border border-[#dbe7ff] bg-[#f8fbff] px-3 py-2 text-[12px] text-[#5f6f89]">
                Select website, page, and embed block to load page embed code.
              </div>
            )}

            {hasSelectedEmbedCodeWebsite && hasSelectedEmbedCodePage && hasNoEmbedBlocks && (
              <div className="rounded-lg border border-[#dbe7ff] bg-[#f8fbff] px-3 py-2 text-[12px] text-[#5f6f89]">
                Selected page has no blocks with role embed.
              </div>
            )}

            {hasSelectedValidEmbedBlock && (
              <>
                <label className={`flex items-center gap-2 rounded-lg border border-[#dbe7ff] bg-[#f8fbff] px-3 py-2 text-[12px] text-[#27344a] ${
                  embedCodeLoading || embedCodeSaving ? 'opacity-60' : ''
                }`}>
                  <input
                    type="checkbox"
                    checked={embedCodeIsolateStyles}
                    onChange={(event) => {
                      setEmbedCodeIsolateStyles(event.target.checked);
                      setEmbedCodeFeedback(null);
                    }}
                    disabled={embedCodeLoading || embedCodeSaving}
                    className="h-4 w-4 rounded border-[#b8c7e6] text-[#165BFB] focus:ring-[#165BFB]"
                  />
                  <span>
                    Isolate styles (CSS/LESS)
                    {embedCodeIsolateStyles !== savedEmbedCodeIsolateStyles && (
                      <span className="ml-1 text-[#dc2626]">*</span>
                    )}
                  </span>
                </label>

                <div className="flex flex-wrap gap-2">
                  {EMBED_CODE_TABS.map((tab) => {
                    const isTabModified = isEmbedCodeTabModified(tab.key);
                    const isTabEmpty = isEmbedCodeTabEmpty(tab.key);

                    return (
                      <button
                        key={tab.key}
                        type="button"
                        onClick={() => setActiveEmbedCodeTab(tab.key)}
                        disabled={embedCodeLoading || embedCodeSaving}
                        className={`rounded-full border px-3 py-1.5 text-[12px] font-semibold transition-colors ${
                          activeEmbedCodeTab === tab.key
                            ? 'border-[#93b5ff] bg-[#eef4ff] text-[#165BFB]'
                            : 'border-[#dbe7ff] bg-white text-[#4b5a73] hover:border-[#b8d1ff]'
                        } ${isTabEmpty ? 'border-dashed' : ''} ${embedCodeLoading || embedCodeSaving ? 'cursor-not-allowed opacity-60' : ''}`}
                      >
                        <span className="inline-flex items-center gap-1.5">
                          <span>{tab.label}</span>
                          {isTabModified && (
                            <span className="text-[#dc2626]" aria-label="Modified tab">*</span>
                          )}
                          {isTabEmpty && (
                            <span className="rounded-full bg-[#eef2ff] px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.04em] text-[#6b7280]">
                              Empty
                            </span>
                          )}
                        </span>
                      </button>
                    );
                  })}
                </div>

                <CustomCodeEditor
                  value={embedCodeValues[activeEmbedCodeTab]}
                  onChange={handleEmbedCodeValueChange}
                  disabled={embedCodeLoading || embedCodeSaving}
                  placeholder={embedCodeLoading ? 'Loading embed code...' : 'Section is empty'}
                  mode={getEmbedCodeEditorMode(activeEmbedCodeTab)}
                />

                <button
                  type="button"
                  onClick={handleSaveEmbedCode}
                  disabled={!canSaveEmbedCode}
                  className={`h-11 rounded-lg font-semibold transition-colors ${
                    canSaveEmbedCode
                      ? 'bg-[#165bfb] text-white hover:bg-[#1246d7] active:bg-[#0d38b0]'
                      : 'bg-[#cdd7ff] text-white cursor-not-allowed'
                  }`}
                >
                  {embedCodeSaving ? 'Saving...' : 'Save embed code'}
                </button>
              </>
            )}

            {embedCodeFeedback && (
              <div className={`text-[12px] ${embedCodeFeedback.type === 'success' ? 'text-[#166534]' : 'text-[#DC2626]'}`}>
                {embedCodeFeedback.message}
              </div>
            )}
            </div>
          )}
        </motion.div>

        {/* Block 3: Block content info */}
        <motion.div className="agent-section bg-white rounded-2xl border-2 border-[#edf0f5] p-5 mb-8 order-2" initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.295 }}>
          {renderCollapsibleSectionHeader('blockContentInfo', 'Block content info')}
          {isBlockContentInfoSectionExpanded && (
            <div id={COLLAPSIBLE_SIDEBAR_SECTION_CONTENT_IDS.blockContentInfo} className="flex flex-col gap-4">
              <div className="flex flex-col gap-2">
                <label className="text-[12px] text-[#7a8496]" htmlFor="block-content-website">
                  Website
                </label>
                <select
                  id="block-content-website"
                  value={selectedBlockContentWebsiteId}
                  onChange={(event) => {
                    setSelectedBlockContentWebsiteId(event.target.value);
                    setBlockContentFeedback(null);
                  }}
                  disabled={customCodeWebsitesLoading || blockContentSummaryLoading}
                  className="w-full border border-[#EDF0F5] h-11 px-4 rounded-lg focus:border-[#165BFB] focus:ring-1 focus:ring-[#165BFB] transition-all outline-none bg-white disabled:bg-[#f6f8ff] disabled:text-[#9CA3AF]"
                >
                  <option value="">
                    {customCodeWebsitesLoading ? 'Loading websites...' : 'Select website'}
                  </option>
                  {customCodeWebsites.map((website) => (
                    <option key={website.id} value={website.id}>
                      {website.name || website.domainLabel}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-[12px] text-[#7a8496]" htmlFor="block-content-page">
                  Page
                </label>
                <select
                  id="block-content-page"
                  value={selectedBlockContentPageId}
                  onChange={(event) => {
                    setSelectedBlockContentPageId(event.target.value);
                    setBlockContentFeedback(null);
                  }}
                  disabled={!hasSelectedBlockContentWebsite || blockContentPagesLoading || blockContentSummaryLoading}
                  className="w-full border border-[#EDF0F5] h-11 px-4 rounded-lg focus:border-[#165BFB] focus:ring-1 focus:ring-[#165BFB] transition-all outline-none bg-white disabled:bg-[#f6f8ff] disabled:text-[#9CA3AF]"
                >
                  <option value="">
                    {!hasSelectedBlockContentWebsite
                      ? 'Select website first'
                      : (blockContentPagesLoading ? 'Loading pages...' : 'Select page')}
                  </option>
                  {blockContentPages.map((page) => (
                    <option key={page.id} value={page.id}>
                      {page.label || page.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-[12px] text-[#7a8496]" htmlFor="block-content-block">
                  Block
                </label>
                <select
                  id="block-content-block"
                  value={selectedBlockContentBlockId}
                  onChange={(event) => {
                    setSelectedBlockContentBlockId(event.target.value);
                    setBlockContentFeedback(null);
                  }}
                  disabled={!hasSelectedBlockContentPage || blockContentBlocksLoading || blockContentSummaryLoading}
                  className="w-full border border-[#EDF0F5] h-11 px-4 rounded-lg focus:border-[#165BFB] focus:ring-1 focus:ring-[#165BFB] transition-all outline-none bg-white disabled:bg-[#f6f8ff] disabled:text-[#9CA3AF]"
                >
                  {!hasSelectedBlockContentPage && (
                    <option value="">Select page first</option>
                  )}
                  {hasSelectedBlockContentPage && blockContentBlocksLoading && (
                    <option value="">Loading blocks...</option>
                  )}
                  {hasSelectedBlockContentPage && !blockContentBlocksLoading && hasNoBlockContentBlocks && (
                    <option value={NO_BLOCK_CONTENT_BLOCK_OPTION_ID}>no blocks</option>
                  )}
                  {hasSelectedBlockContentPage && !blockContentBlocksLoading && !hasNoBlockContentBlocks && (
                    <>
                      <option value="">Select block</option>
                      {blockContentBlocks.map((block) => (
                        <option key={block.id} value={block.id}>
                          {getBlockDisplayName(block)}
                        </option>
                      ))}
                    </>
                  )}
                </select>
              </div>

              {customCodeWebsitesError && (
                <div className="text-[12px] text-[#dc2626]">{customCodeWebsitesError}</div>
              )}
              {blockContentPagesError && (
                <div className="text-[12px] text-[#dc2626]">{blockContentPagesError}</div>
              )}
              {blockContentBlocksError && (
                <div className="text-[12px] text-[#dc2626]">{blockContentBlocksError}</div>
              )}

              <div className="rounded-xl border border-[#dbe7ff] bg-[#f8fbff] px-3 py-2">
                <div className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[#4f6ea8]">Selected targets</div>
                <div className="mt-1 text-[12px] text-[#27344a]">
                  Website: {selectedBlockContentWebsite ? selectedBlockContentWebsite.name || selectedBlockContentWebsite.domainLabel : 'Not selected'}
                </div>
                <div className="mt-0.5 text-[12px] text-[#27344a]">
                  Page: {selectedBlockContentPage ? selectedBlockContentPage.label || selectedBlockContentPage.name : 'Not selected'}
                </div>
                <div className="mt-0.5 text-[12px] text-[#27344a]">
                  Block: {selectedBlockContentBlock ? getBlockDisplayName(selectedBlockContentBlock) : (hasNoBlockContentBlocks ? 'no blocks' : 'Not selected')}
                </div>
              </div>

              {!hasSelectedBlockContentWebsite && (
                <div className="rounded-lg border border-[#dbe7ff] bg-[#f8fbff] px-3 py-2 text-[12px] text-[#5f6f89]">
                  Select website, page, and block to inspect block content.
                </div>
              )}

              {hasSelectedBlockContentWebsite && hasSelectedBlockContentPage && hasNoBlockContentBlocks && (
                <div className="rounded-lg border border-[#dbe7ff] bg-[#f8fbff] px-3 py-2 text-[12px] text-[#5f6f89]">
                  Selected page has no blocks.
                </div>
              )}

              {hasSelectedValidBlockContentBlock && (
                <>
                  <div className="rounded-xl border border-[#dbe7ff] bg-[#f8fbff] px-3 py-2">
                    <div className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[#4f6ea8]">
                      Elements count
                    </div>
                    <div className="mt-1 text-[13px] font-semibold text-[#27344a]">
                      Elements count:{' '}
                      {blockContentSummaryLoading ? 'Loading...' : (blockContentSummary?.flatElementsListItemsCount ?? '—')}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    {BLOCK_CONTENT_VIEW_BUTTONS.map((view) => (
                      <button
                        key={view.key}
                        type="button"
                        onClick={() => {
                          void handleOpenBlockContentPopup(view.key);
                        }}
                        disabled={blockContentSummaryLoading || blockContentPopupState.isLoading}
                        className={`h-10 rounded-lg border px-3 text-left text-[12px] font-semibold transition-colors ${
                          blockContentSummaryLoading || blockContentPopupState.isLoading
                            ? 'cursor-not-allowed border-[#dbe7ff] bg-[#f1f5ff] text-[#9CA3AF]'
                            : 'border-[#cfe0ff] bg-white text-[#165BFB] hover:border-[#97b9ff] hover:bg-[#f5f9ff]'
                        }`}
                      >
                        {view.label}
                      </button>
                    ))}
                  </div>
                </>
              )}

              {blockContentFeedback && (
                <div className={`text-[12px] ${blockContentFeedback.type === 'success' ? 'text-[#166534]' : 'text-[#DC2626]'}`}>
                  {blockContentFeedback.message}
                </div>
              )}
            </div>
          )}
        </motion.div>

        {/* Block 4: Push block to Weblium */}
        <motion.div className="agent-section bg-white rounded-2xl border-2 border-[#edf0f5] p-5 mb-8 order-2" initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.298 }}>
          {renderCollapsibleSectionHeader('pushBlockToWeblium', 'Push block to Weblium')}
          {isPushBlockToWebliumSectionExpanded && (
            <div id={COLLAPSIBLE_SIDEBAR_SECTION_CONTENT_IDS.pushBlockToWeblium} className="flex flex-col gap-4">
              <div className="rounded-lg border border-[#f5d5a7] bg-[#fffaf0] px-3 py-2 text-[12px] text-[#805b20]">
                This replaces the selected block content in Weblium.
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-[12px] text-[#7a8496]" htmlFor="push-block-website">Website</label>
                <select
                  id="push-block-website"
                  value={selectedPushWebsiteId}
                  onChange={(event) => {
                    setSelectedPushWebsiteId(event.target.value);
                    setBlockPushFeedback(null);
                  }}
                  disabled={customCodeWebsitesLoading || blockPushSubmitting}
                  className="w-full border border-[#EDF0F5] h-11 px-4 rounded-lg focus:border-[#165BFB] focus:ring-1 focus:ring-[#165BFB] transition-all outline-none bg-white disabled:bg-[#f6f8ff] disabled:text-[#9CA3AF]"
                >
                  <option value="">{customCodeWebsitesLoading ? 'Loading websites...' : 'Select website'}</option>
                  {customCodeWebsites.map((website) => (
                    <option key={website.id} value={website.id}>
                      {website.name || website.domainLabel}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-[12px] text-[#7a8496]" htmlFor="push-block-page">Page</label>
                <select
                  id="push-block-page"
                  value={selectedPushPageId}
                  onChange={(event) => {
                    setSelectedPushPageId(event.target.value);
                    setBlockPushFeedback(null);
                  }}
                  disabled={!hasSelectedPushWebsite || pushPagesLoading || blockPushSubmitting}
                  className="w-full border border-[#EDF0F5] h-11 px-4 rounded-lg focus:border-[#165BFB] focus:ring-1 focus:ring-[#165BFB] transition-all outline-none bg-white disabled:bg-[#f6f8ff] disabled:text-[#9CA3AF]"
                >
                  <option value="">
                    {!hasSelectedPushWebsite ? 'Select website first' : (pushPagesLoading ? 'Loading pages...' : 'Select page')}
                  </option>
                  {pushPages.map((page) => (
                    <option key={page.id} value={page.id}>
                      {page.label || page.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-[12px] text-[#7a8496]" htmlFor="push-block-block">Block</label>
                <select
                  id="push-block-block"
                  value={selectedPushBlockId}
                  onChange={(event) => {
                    setSelectedPushBlockId(event.target.value);
                    setBlockPushFeedback(null);
                  }}
                  disabled={!hasSelectedPushPage || pushBlocksLoading || blockPushSubmitting}
                  className="w-full border border-[#EDF0F5] h-11 px-4 rounded-lg focus:border-[#165BFB] focus:ring-1 focus:ring-[#165BFB] transition-all outline-none bg-white disabled:bg-[#f6f8ff] disabled:text-[#9CA3AF]"
                >
                  {!hasSelectedPushPage && <option value="">Select page first</option>}
                  {hasSelectedPushPage && pushBlocksLoading && <option value="">Loading blocks...</option>}
                  {hasSelectedPushPage && !pushBlocksLoading && hasNoPushBlocks && (
                    <option value={NO_PUSH_BLOCK_OPTION_ID}>no blocks</option>
                  )}
                  {hasSelectedPushPage && !pushBlocksLoading && !hasNoPushBlocks && (
                    <>
                      <option value="">Select block</option>
                      {pushBlocks.map((block) => (
                        <option key={block.id} value={block.id}>
                          {getBlockDisplayName(block)}
                        </option>
                      ))}
                    </>
                  )}
                </select>
              </div>

              {customCodeWebsitesError && <div className="text-[12px] text-[#dc2626]">{customCodeWebsitesError}</div>}
              {pushPagesError && <div className="text-[12px] text-[#dc2626]">{pushPagesError}</div>}
              {pushBlocksError && <div className="text-[12px] text-[#dc2626]">{pushBlocksError}</div>}

              <div className="rounded-xl border border-[#dbe7ff] bg-[#f8fbff] px-3 py-2">
                <div className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[#4f6ea8]">Selected targets</div>
                <div className="mt-1 text-[12px] text-[#27344a]">
                  Website: {selectedPushWebsite ? selectedPushWebsite.name || selectedPushWebsite.domainLabel : 'Not selected'}
                </div>
                <div className="mt-0.5 text-[12px] text-[#27344a]">
                  Page: {selectedPushPage ? selectedPushPage.label || selectedPushPage.name : 'Not selected'}
                </div>
                <div className="mt-0.5 text-[12px] text-[#27344a]">
                  Block: {selectedPushBlock ? getBlockDisplayName(selectedPushBlock) : (hasNoPushBlocks ? 'no blocks' : 'Not selected')}
                </div>
              </div>

              <button
                type="button"
                onClick={() => {
                  void handlePushBlockToWeblium();
                }}
                disabled={!canPushBlock}
                className={`h-11 rounded-lg font-semibold transition-colors ${
                  canPushBlock
                    ? 'bg-[#165bfb] text-white hover:bg-[#1246d7] active:bg-[#0d38b0]'
                    : 'bg-[#cdd7ff] text-white cursor-not-allowed'
                }`}
              >
                {blockPushSubmitting ? 'Pushing...' : 'Push'}
              </button>

              {blockPushFeedback && (
                <div className={`text-[12px] ${blockPushFeedback.type === 'success' ? 'text-[#166534]' : 'text-[#DC2626]'}`}>
                  {blockPushFeedback.message}
                </div>
              )}
            </div>
          )}
        </motion.div>

        {/* Block 5: Session context */}
        <motion.div className="agent-section bg-white rounded-2xl border-2 border-[#edf0f5] p-5 mb-8 order-5" initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.3 }}>
          {renderCollapsibleSectionHeader('sessionContext', 'Session context')}
          {isSessionContextSectionExpanded && (
            <form id={COLLAPSIBLE_SIDEBAR_SECTION_CONTENT_IDS.sessionContext} className="flex flex-col gap-4" onSubmit={handleContextSubmit}>
              <div className="flex flex-col gap-2">
                <label className="text-[12px] text-[#7a8496]" htmlFor="session-context-website">
                  Website ID
                </label>
                <input
                  id="session-context-website"
                  type="text"
                  value={websiteId}
                  disabled={isContextDisabled}
                  onChange={(event) => {
                    setWebsiteId(event.target.value);
                    setContextFeedback(null);
                  }}
                  placeholder={selectedSession ? 'Enter website id' : 'Select a session first'}
                  className="w-full border border-[#EDF0F5] h-11 px-4 rounded-lg focus:border-[#165BFB] focus:ring-1 focus:ring-[#165BFB] transition-all outline-none disabled:bg-[#f6f8ff] disabled:text-[#9CA3AF]"
                />
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-[12px] text-[#7a8496]" htmlFor="session-context-page">
                  Web portal section
                </label>
                <select
                  id="session-context-page"
                  value={pageName}
                  disabled={isContextDisabled}
                  onChange={(event) => {
                    setPageName(event.target.value as PageNameOption | '');
                    setContextFeedback(null);
                  }}
                  className="w-full border border-[#EDF0F5] h-11 px-4 rounded-lg focus:border-[#165BFB] focus:ring-1 focus:ring-[#165BFB] transition-all outline-none disabled:bg-[#f6f8ff] disabled:text-[#9CA3AF] bg-white"
                >
                  <option value="">{selectedSession ? 'Not set' : 'Select a session first'}</option>
                  {PAGE_NAME_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>
              <button
                type="submit"
                disabled={!canSubmitContext}
                className={`h-11 rounded-lg font-semibold transition-colors ${
                  canSubmitContext
                    ? 'bg-[#165bfb] text-white hover:bg-[#1246d7] active:bg-[#0d38b0]'
                    : 'bg-[#cdd7ff] text-white cursor-not-allowed'
                }`}
              >
                {contextSubmitting ? 'Saving...' : 'Save context'}
              </button>
              {contextFeedback && (
                <div
                  className={`text-[12px] ${
                    contextFeedback.type === 'success' ? 'text-[#166534]' : 'text-[#DC2626]'
                  }`}
                >
                  {contextFeedback.message}
                </div>
              )}
            </form>
          )}
        </motion.div>

        {/* Block 6: Session data */}
        <motion.div className="agent-section bg-white rounded-2xl border-2 border-[#edf0f5] p-5 mb-8 order-6" initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.32 }}>
          {renderCollapsibleSectionHeader('sessionData', 'Session data')}
          {isSessionDataSectionExpanded && (
            <div id={COLLAPSIBLE_SIDEBAR_SECTION_CONTENT_IDS.sessionData} className="flex flex-col gap-4">
              <div className="agent-inset bg-[#f7f9ff] rounded-xl p-4 border border-[#edf0f5]">
                <div className="text-[12px] text-[#7a8496] uppercase tracking-wide mb-2">Current context</div>
                {sessionCurrentContext ? (
                  <div className="text-sm text-[#202c42] space-y-1">
                    <div><span className="font-semibold text-[#464C5C]">Website ID:</span> {formatContextValue(sessionCurrentContext.websiteId)}</div>
                    <div><span className="font-semibold text-[#464C5C]">Web portal section:</span> {formatContextValue(sessionCurrentContext.pageName)}</div>
                  </div>
                ) : (
                  <div className="text-sm text-[#6B7280]">Session context is empty.</div>
                )}
              </div>
              <div className="agent-inset bg-[#f7f9ff] rounded-xl p-4 border border-[#edf0f5]">
                <div className="text-[12px] text-[#7a8496] uppercase tracking-wide mb-2">Context history</div>
                {sessionContextHistory.length > 0 ? (
                  <ul className="space-y-3">
                    {sessionContextHistory.map((contextItem, index) => (
                      <li key={`${contextItem.websiteId ?? 'null'}-${contextItem.pageName ?? 'null'}-${index}`} className="text-sm text-[#202c42] bg-white border border-[#e8edf7] rounded-lg p-3">
                        <div><span className="font-semibold text-[#464C5C]">Website ID:</span> {formatContextValue(contextItem.websiteId)}</div>
                        <div><span className="font-semibold text-[#464C5C]">Web portal section:</span> {formatContextValue(contextItem.pageName)}</div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="text-sm text-[#6B7280]">No context history yet.</div>
                )}
              </div>
            </div>
          )}
        </motion.div>

        {/* Block 7: Session Rename */}
        <motion.div className="agent-section bg-white rounded-2xl border-2 border-[#edf0f5] p-5 mb-8 order-7" initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.325 }}>
          {renderCollapsibleSectionHeader('sessionRename', 'Session Rename')}
          {isSessionRenameSectionExpanded && (
          <div id={COLLAPSIBLE_SIDEBAR_SECTION_CONTENT_IDS.sessionRename} className="flex flex-col gap-4">
            {sessionRenameOptions || renamedSessionName ? (
              <div className="space-y-3">
                <div className="agent-inset bg-[#f7f9ff] rounded-xl p-4 border border-[#edf0f5]">
                  <div className="text-xs text-[#6B7280] space-y-1">
                    <div><span className="font-semibold text-[#464C5C]">Session name:</span> {renamedSessionName || selectedSession?.name || '—'}</div>
                    {sessionRenameOptions && (
                      <>
                        <div><span className="font-semibold text-[#464C5C]">Updated:</span> {formatSummaryTimestamp(sessionRenameOptions.timestamp)}</div>
                        <div><span className="font-semibold text-[#464C5C]">Messages used:</span> {sessionRenameOptions.userMessagesUsed}</div>
                        <div><span className="font-semibold text-[#464C5C]">Regenerations:</span> {sessionRenameOptions.regenerationCount}</div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-sm text-[#6B7280]">Session name not generated yet.</div>
            )}
            {renameFeedback && (
              <div className={`text-[12px] ${renameFeedback.type === 'success' ? 'text-[#166534]' : 'text-[#DC2626]'}`}>
                {renameFeedback.message}
              </div>
            )}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => handleRename(false)}
                disabled={!selectedSession || renameSubmitting}
                className={`flex-1 h-11 rounded-lg font-semibold transition-colors ${
                  !selectedSession || renameSubmitting
                    ? 'bg-[#cdd7ff] text-white cursor-not-allowed'
                    : 'bg-[#165bfb] text-white hover:bg-[#1246d7] active:bg-[#0d38b0]'
                }`}
              >
                {renameSubmitting ? 'Renaming…' : 'Rename'}
              </button>
              <button
                type="button"
                onClick={() => handleRename(true)}
                disabled={!selectedSession || renameSubmitting}
                className={`flex-1 h-11 rounded-lg font-semibold transition-colors ${
                  !selectedSession || renameSubmitting
                    ? 'bg-[#cdd7ff] text-white cursor-not-allowed'
                    : 'bg-[#165bfb] text-white hover:bg-[#1246d7] active:bg-[#0d38b0]'
                }`}
              >
                {renameSubmitting ? 'Renaming…' : 'Force Rename'}
              </button>
            </div>
          </div>
          )}
        </motion.div>

        {/* Block 8: Summary */}
        <motion.div className="agent-section bg-white rounded-2xl border-2 border-[#edf0f5] p-5 mb-8 order-8" initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.33 }}>
          {renderCollapsibleSectionHeader('summary', 'Summary')}
          {isSummarySectionExpanded && (
          <div id={COLLAPSIBLE_SIDEBAR_SECTION_CONTENT_IDS.summary} className="flex flex-col gap-4">
            {sessionSummary ? (
              <div className="space-y-3">
                <div className="agent-inset bg-[#f7f9ff] rounded-xl p-4 border border-[#edf0f5] max-h-48 overflow-auto whitespace-pre-line text-sm text-[#202c42]">
                  {sessionSummary.text}
                </div>
                <div className="text-xs text-[#6B7280]">
                  <div><span className="font-semibold text-[#464C5C]">Updated:</span> {formatSummaryTimestamp(sessionSummary.timestamp)}</div>
                </div>
              </div>
            ) : (
              <div className="text-sm text-[#6B7280]">Summary is not set yet.</div>
            )}
            {summaryFeedback && (
              <div className={`text-[12px] ${summaryFeedback.type === 'success' ? 'text-[#166534]' : 'text-[#DC2626]'}`}>
                {summaryFeedback.message}
              </div>
            )}
            <button
              type="button"
              onClick={handleSummarize}
              disabled={!selectedSession || summarySubmitting}
              className={`h-11 rounded-lg font-semibold transition-colors ${
                !selectedSession || summarySubmitting
                  ? 'bg-[#cdd7ff] text-white cursor-not-allowed'
                  : 'bg-[#165bfb] text-white hover:bg-[#1246d7] active:bg-[#0d38b0]'
              }`}
            >
              {summarySubmitting ? 'Summarizing…' : 'Summarize'}
            </button>
          </div>
          )}
        </motion.div>

        {/* Block 9: Available Tools */}
        {selectedSession?.type !== 'vibe_site' && (
        <motion.div className="agent-section bg-white rounded-2xl border-2 border-[#edf0f5] p-5 mb-8 order-9" initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.34 }}>
          {renderCollapsibleSectionHeader('availableTools', 'Available Tools')}
          {isAvailableToolsSectionExpanded && (
            <div id={COLLAPSIBLE_SIDEBAR_SECTION_CONTENT_IDS.availableTools} className="space-y-3">
            <input
              ref={availableToolsFilterInputRef}
              type="text"
              value={availableToolsFilter}
              onChange={(event) => setAvailableToolsFilter(event.target.value)}
              placeholder="Filter tools"
              aria-label="Filter available tools"
              className="h-11 w-full rounded-lg border border-[#dbe7ff] bg-[#f9fbff] px-3 text-[14px] text-[#202c42] outline-none transition-colors placeholder:text-[#7a8496] focus:border-[#165bfb] focus:bg-white"
            />
            <div className="flex flex-wrap items-center gap-2 text-[12px]">
              <div className="rounded-full border border-[#cfe0ff] bg-[#eef4ff] px-3 py-1 text-[#4f6ea8]">
                After filtering: <span className="font-semibold text-[#165bfb]">{filteredAvailableTools.length}</span>
              </div>
              <div className="rounded-full border border-[#edf0f5] bg-[#f9fbff] px-3 py-1 text-[#7a8496]">
                Total: <span className="font-semibold text-[#464c5c]">{availableTools.length}</span>
              </div>
            </div>
            {availableTools.length === 0 && (
              <div className="text-[14px] text-[#7a8496]">No tools available</div>
            )}
            {availableTools.length > 0 && filteredAvailableTools.length === 0 && (
              <div className="text-[14px] text-[#7a8496]">No tools match your filter</div>
            )}
            {filteredAvailableTools.map((tool) => (
              <label key={tool.name} className="agent-tool-option flex items-center gap-3 bg-[#f9fbff] p-3 rounded-lg border border-[#edf0f5]">
                <input
                  type="checkbox"
                  checked={selectedTools.has(tool.name)}
                  onChange={() => toggleTool(tool.name)}
                  className="size-4 accent-[#165bfb]"
                />
                <div>
                  <div className="text-[14px] font-medium text-[#202c42] leading-[18px]">{tool.name}</div>
                  <div className="text-[12px] text-[#7a8496] leading-[16px]">{tool.description}</div>
                </div>
              </label>
            ))}
            </div>
          )}
        </motion.div>

        )}

        {/* Block 2: Last Tools Calls */}
        <motion.div className="agent-section bg-white rounded-2xl border-2 border-[#edf0f5] p-5 mb-8 order-3" initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.4 }}>
          {renderCollapsibleSectionHeader('lastToolCalls', 'Last Tools Calls')}
          {isLastToolCallsSectionExpanded && (
            <div id={COLLAPSIBLE_SIDEBAR_SECTION_CONTENT_IDS.lastToolCalls}>
              {(!lastToolsCalls || lastToolsCalls.length === 0) && (
                <div className="text-[14px] text-[#7a8496]">No tool calls yet</div>
              )}

              {lastToolsCalls && lastToolsCalls.length > 0 && (
                <div className="space-y-4">
                  {lastToolsCalls.map((call, index) => {
                    const callKey = `${call.id}-${index}`;
                    const isExpanded = expandedToolCalls.has(callKey);

                    return (
                      <div key={callKey} className="rounded-xl border border-[#edf0f5] p-4 bg-[#f9fbff] min-w-0 overflow-hidden">
                        <div className="flex items-center justify-between gap-3">
                          <div className="text-[14px] font-semibold text-[#202c42] truncate">
                            {call.name}
                          </div>
                          <button
                            type="button"
                            onClick={() => toggleToolCallExpansion(callKey)}
                            aria-expanded={isExpanded}
                            className="shrink-0 rounded-md border border-[#cfe0ff] bg-white px-3 py-1.5 text-[12px] font-semibold text-[#165bfb] transition-colors hover:bg-[#eef4ff]"
                          >
                            {isExpanded ? 'Collapse' : 'Expand'}
                          </button>
                        </div>

                        {isExpanded && (
                          <div className="grid gap-2 mt-3 min-w-0">
                            <div className="min-w-0">
                              <div className="text-[12px] text-[#7a8496] mb-1">Name</div>
                              <pre className="w-full max-w-full text-[12px] p-2 bg-white rounded border border-[#e8edf7] overflow-x-auto whitespace-pre-wrap break-all">{call.name}</pre>
                            </div>
                            <div className="min-w-0">
                              <div className="text-[12px] text-[#7a8496] mb-1">ID</div>
                              <pre className="w-full max-w-full text-[12px] p-2 bg-white rounded border border-[#e8edf7] overflow-x-auto whitespace-pre-wrap break-all">{call.id}</pre>
                            </div>
                            <div className="min-w-0">
                              <div className="text-[12px] text-[#7a8496] mb-1">Arguments</div>
                              <pre className="w-full max-w-full max-h-40 text-[12px] p-2 bg-white rounded border border-[#e8edf7] overflow-auto whitespace-pre-wrap break-all">{call.arguments}</pre>
                            </div>
                            <div className="min-w-0">
                              <div className="mb-1 flex items-center justify-between gap-2">
                                <div className="text-[12px] text-[#7a8496]">Result</div>
                                <button
                                  type="button"
                                  className="inline-flex size-6 items-center justify-center rounded-md border border-[#d7e3ff] bg-[#f8fbff] text-[#4f6ea8] transition-colors hover:border-[#97b9ff] hover:text-[#165BFB]"
                                  onClick={() => {
                                    void handleCopyToolResult(call.result, callKey);
                                  }}
                                  title={copiedToolResultKey === callKey ? 'Copied' : 'Copy result'}
                                  aria-label="Copy tool result"
                                >
                                  {copiedToolResultKey === callKey ? (
                                    <svg className="size-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                      <path d="M20 6 9 17l-5-5" />
                                    </svg>
                                  ) : (
                                    <svg className="size-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                                    </svg>
                                  )}
                                </button>
                              </div>
                              <pre className="w-full max-w-full max-h-64 text-[12px] p-2 bg-white rounded border border-[#e8edf7] overflow-auto whitespace-pre-wrap break-all">{call.result}</pre>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </motion.div>

        {/* Block 3: Statistics */}
        <motion.div className="agent-section bg-white rounded-2xl border-2 border-[#edf0f5] p-5 mb-8 order-3" initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.38 }}>
          {renderCollapsibleSectionHeader('statistics', 'Statistics')}
          {isStatisticsSectionExpanded && (
            <div id={COLLAPSIBLE_SIDEBAR_SECTION_CONTENT_IDS.statistics} className="flex flex-col gap-3">
              <div className="bg-[#f7f9ff] rounded-xl p-3 border border-[#edf0f5]">
                <div className="text-[12px] text-[#7a8496] mb-1">Tokens used</div>
                <div className="text-[12px] p-2 bg-white rounded border border-[#e8edf7] overflow-x-auto whitespace-pre-wrap break-words">
                  {sessionUsage ? (
                    <div className="space-y-1">
                      <div><span className="font-semibold text-[#464C5C]">Input:</span> {sessionUsage.inputTokensUsed.toLocaleString()}</div>
                      <div><span className="font-semibold text-[#464C5C]">Output:</span> {sessionUsage.outputTokensUsed.toLocaleString()}</div>
                      <div><span className="font-semibold text-[#464C5C]">Total:</span> {(sessionUsage.inputTokensUsed + sessionUsage.outputTokensUsed).toLocaleString()}</div>
                    </div>
                  ) : (
                    displayTokens || 'N/A'
                  )}
                </div>
              </div>
            </div>
          )}
        </motion.div>

        {/* Block 4: Limits */}
        <motion.div className="agent-section bg-white rounded-2xl border-2 border-[#edf0f5] p-5 mb-8 order-4" initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.4 }}>
          {renderCollapsibleSectionHeader('limits', 'Limits')}
          {isLimitsSectionExpanded && (
            <div id={COLLAPSIBLE_SIDEBAR_SECTION_CONTENT_IDS.limits}>
              {sessionUsage ? (
                <div className="flex flex-col gap-4">
              {/* Limit Class Badge */}
              <div className="flex items-center gap-2">
                <span className="text-[12px] text-[#7a8496]">Limit Class:</span>
                <span className="px-2.5 py-1 text-[12px] font-semibold rounded-full bg-[#165bfb] text-white">
                  {sessionUsage.limitClass}
                </span>
              </div>

              {/* Messages Usage */}
              <div className="agent-inset bg-[#f7f9ff] rounded-xl p-4 border border-[#edf0f5]">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-[12px] text-[#7a8496] uppercase tracking-wide">Messages</span>
                  <span className="text-[12px] font-semibold text-[#202c42]">
                    {sessionUsage.messagesUsed} / {sessionUsage.maxMessages}
                  </span>
                </div>
                <div className="w-full h-2 bg-[#e5e7eb] rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-300 ${
                      sessionUsage.messagesUsed / sessionUsage.maxMessages > 0.9
                        ? 'bg-[#ef4444]'
                        : sessionUsage.messagesUsed / sessionUsage.maxMessages > 0.7
                        ? 'bg-[#f59e0b]'
                        : 'bg-[#22c55e]'
                    }`}
                    style={{ width: `${Math.min(100, (sessionUsage.messagesUsed / sessionUsage.maxMessages) * 100)}%` }}
                  />
                </div>
                <div className="text-[10px] text-[#6b7280] mt-1">
                  {sessionUsage.messagesRemaining} remaining
                </div>
              </div>

              {/* Input Tokens Usage */}
              <div className="agent-inset bg-[#f7f9ff] rounded-xl p-4 border border-[#edf0f5]">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-[12px] text-[#7a8496] uppercase tracking-wide">Input Tokens</span>
                  <span className="text-[12px] font-semibold text-[#202c42]">
                    {sessionUsage.inputTokensUsed.toLocaleString()} / {sessionUsage.maxInputTokens.toLocaleString()}
                  </span>
                </div>
                <div className="w-full h-2 bg-[#e5e7eb] rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-300 ${
                      sessionUsage.inputTokensUsed / sessionUsage.maxInputTokens > 0.9
                        ? 'bg-[#ef4444]'
                        : sessionUsage.inputTokensUsed / sessionUsage.maxInputTokens > 0.7
                        ? 'bg-[#f59e0b]'
                        : 'bg-[#22c55e]'
                    }`}
                    style={{ width: `${Math.min(100, (sessionUsage.inputTokensUsed / sessionUsage.maxInputTokens) * 100)}%` }}
                  />
                </div>
                <div className="text-[10px] text-[#6b7280] mt-1">
                  {sessionUsage.inputTokensRemaining.toLocaleString()} remaining
                </div>
              </div>

              {/* Output Tokens Usage */}
              <div className="agent-inset bg-[#f7f9ff] rounded-xl p-4 border border-[#edf0f5]">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-[12px] text-[#7a8496] uppercase tracking-wide">Output Tokens</span>
                  <span className="text-[12px] font-semibold text-[#202c42]">
                    {sessionUsage.outputTokensUsed.toLocaleString()} / {sessionUsage.maxOutputTokens.toLocaleString()}
                  </span>
                </div>
                <div className="w-full h-2 bg-[#e5e7eb] rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-300 ${
                      sessionUsage.outputTokensUsed / sessionUsage.maxOutputTokens > 0.9
                        ? 'bg-[#ef4444]'
                        : sessionUsage.outputTokensUsed / sessionUsage.maxOutputTokens > 0.7
                        ? 'bg-[#f59e0b]'
                        : 'bg-[#22c55e]'
                    }`}
                    style={{ width: `${Math.min(100, (sessionUsage.outputTokensUsed / sessionUsage.maxOutputTokens) * 100)}%` }}
                  />
                </div>
                <div className="text-[10px] text-[#6b7280] mt-1">
                  {sessionUsage.outputTokensRemaining.toLocaleString()} remaining
                </div>
              </div>
                </div>
              ) : (
                <div className="text-[14px] text-[#7a8496]">No limits data available. Select a session to view limits.</div>
              )}
            </div>
          )}
        </motion.div>

        {/* Block 10: System diagnostics */}
        <motion.div className="agent-section bg-white rounded-2xl border-2 border-[#edf0f5] p-5 order-10" initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.42 }}>
          {renderCollapsibleSectionHeader('systemDiagnostics', 'System diagnostics')}
          {isSystemDiagnosticsSectionExpanded && (
            <div id={COLLAPSIBLE_SIDEBAR_SECTION_CONTENT_IDS.systemDiagnostics} className="flex flex-col gap-4">
              <WebsocketDiagnostics />
              <div className="rounded-xl border border-[#dbe7ff] bg-[#f8fbff] p-3">
                <div className="mb-3 flex items-center justify-between gap-3">
                  <div>
                    <div className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[#4f6ea8]">
                      Connection hold
                    </div>
                    <div className="mt-1 text-[13px] font-semibold text-[#27344a]">
                      {systemConnectionTestElapsedSeconds}s
                    </div>
                  </div>
                  <span className={`shrink-0 rounded-full px-2.5 py-1 text-[11px] font-semibold ${
                    systemConnectionTestRunning
                      ? 'bg-[#eef4ff] text-[#165BFB]'
                      : systemConnectionTestError
                      ? 'bg-[#fff1f2] text-[#b4232f]'
                      : systemConnectionTestResult
                      ? 'bg-[#ecfdf3] text-[#166534]'
                      : 'bg-white text-[#5f6f89]'
                  }`}>
                    {systemConnectionTestRunning
                      ? 'Running'
                      : systemConnectionTestError
                      ? 'Failed'
                      : systemConnectionTestResult
                      ? 'Completed'
                      : 'Ready'}
                  </span>
                </div>

                <button
                  type="button"
                  onClick={() => {
                    void handleRunSystemConnectionTest();
                  }}
                  disabled={systemConnectionTestRunning}
                  className={`h-11 w-full rounded-lg font-semibold transition-colors ${
                    systemConnectionTestRunning
                      ? 'cursor-not-allowed bg-[#cdd7ff] text-white'
                      : 'bg-[#165bfb] text-white hover:bg-[#1246d7] active:bg-[#0d38b0]'
                  }`}
                >
                  {systemConnectionTestRunning ? 'Waiting for response...' : 'Run 6-minute request'}
                </button>

                {systemConnectionTestError && (
                  <div className="mt-3 rounded-lg border border-[#fecaca] bg-[#fff1f2] px-3 py-2 text-[12px] text-[#b4232f]">
                    {systemConnectionTestError}
                  </div>
                )}

                {systemConnectionTestResult && (
                  <pre className="mt-3 max-h-48 overflow-auto rounded-lg border border-[#e8edf7] bg-white p-3 text-[12px] text-[#27344a] whitespace-pre-wrap break-all">
                    {stringifyBlockContentValue(systemConnectionTestResult)}
                  </pre>
                )}
              </div>

              <div className="rounded-xl border border-[#dbe7ff] bg-[#f8fbff] p-3">
                <div className="mb-3 flex items-center justify-between gap-3">
                  <div>
                    <div className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[#4f6ea8]">
                      Environment config
                    </div>
                    <div className="mt-1 text-[13px] font-semibold text-[#27344a]">
                      {systemConfigResult ? systemConfigResult.envName : 'Not loaded'}
                    </div>
                  </div>
                  <span className={`shrink-0 rounded-full px-2.5 py-1 text-[11px] font-semibold ${
                    systemConfigLoading
                      ? 'bg-[#eef4ff] text-[#165BFB]'
                      : systemConfigError
                      ? 'bg-[#fff1f2] text-[#b4232f]'
                      : systemConfigResult
                      ? 'bg-[#ecfdf3] text-[#166534]'
                      : 'bg-white text-[#5f6f89]'
                  }`}>
                    {systemConfigLoading
                      ? 'Loading'
                      : systemConfigError
                      ? 'Failed'
                      : systemConfigResult
                      ? 'Loaded'
                      : 'Ready'}
                  </span>
                </div>

                <button
                  type="button"
                  onClick={() => {
                    void handleLoadSystemConfig();
                  }}
                  disabled={systemConfigLoading}
                  className={`h-11 w-full rounded-lg font-semibold transition-colors ${
                    systemConfigLoading
                      ? 'cursor-not-allowed bg-[#cdd7ff] text-white'
                      : 'bg-[#165bfb] text-white hover:bg-[#1246d7] active:bg-[#0d38b0]'
                  }`}
                >
                  {systemConfigLoading ? 'Loading config...' : 'Load config'}
                </button>

                {systemConfigError && (
                  <div className="mt-3 rounded-lg border border-[#fecaca] bg-[#fff1f2] px-3 py-2 text-[12px] text-[#b4232f]">
                    {systemConfigError}
                  </div>
                )}

                {systemConfigResult && (
                  <div className="mt-3 rounded-lg border border-[#e8edf7] bg-white p-3 text-[12px] text-[#27344a]">
                    <div className="flex items-start justify-between gap-3">
                      <span className="shrink-0 font-semibold text-[#5f6f89]">Env name</span>
                      <span className="min-w-0 break-all text-right">{systemConfigResult.envName || '-'}</span>
                    </div>
                    <div className="mt-2 flex items-start justify-between gap-3">
                      <span className="shrink-0 font-semibold text-[#5f6f89]">Env comment</span>
                      <span className="min-w-0 break-all text-right">{systemConfigResult.envComment || '-'}</span>
                    </div>
                  </div>
                )}
              </div>

              <div className="rounded-xl border border-[#dbe7ff] bg-[#f8fbff] p-3">
                <div className="mb-3 flex items-center justify-between gap-3">
                  <div>
                    <div className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[#4f6ea8]">
                      Weblium authentication
                    </div>
                    <div className="mt-1 text-[13px] font-semibold text-[#27344a]">
                      {systemWebliumAuthResult
                        ? `HTTP ${systemWebliumAuthResult.upstreamStatus}`
                        : 'Not checked'}
                    </div>
                  </div>
                  <span className={`shrink-0 rounded-full px-2.5 py-1 text-[11px] font-semibold ${
                    systemWebliumAuthLoading
                      ? 'bg-[#eef4ff] text-[#165BFB]'
                      : systemWebliumAuthError
                      ? 'bg-[#fff1f2] text-[#b4232f]'
                      : systemWebliumAuthResult
                      ? 'bg-[#ecfdf3] text-[#166534]'
                      : 'bg-white text-[#5f6f89]'
                  }`}>
                    {systemWebliumAuthLoading
                      ? 'Checking'
                      : systemWebliumAuthError
                      ? 'Failed'
                      : systemWebliumAuthResult
                      ? 'Succeeded'
                      : 'Ready'}
                  </span>
                </div>

                <button
                  type="button"
                  onClick={() => {
                    void handleCheckSystemWebliumAuth();
                  }}
                  disabled={systemWebliumAuthLoading}
                  className={`h-11 w-full rounded-lg font-semibold transition-colors ${
                    systemWebliumAuthLoading
                      ? 'cursor-not-allowed bg-[#cdd7ff] text-white'
                      : 'bg-[#165bfb] text-white hover:bg-[#1246d7] active:bg-[#0d38b0]'
                  }`}
                >
                  {systemWebliumAuthLoading ? 'Checking Weblium Auth...' : 'check Weblium Auth'}
                </button>

                {systemWebliumAuthError && (
                  <div className="mt-3 rounded-lg border border-[#fecaca] bg-[#fff1f2] px-3 py-2 text-[12px] text-[#b4232f]">
                    {systemWebliumAuthError}
                  </div>
                )}

                {systemWebliumAuthResult && (
                  <div className="mt-3 rounded-lg border border-[#bbf7d0] bg-[#ecfdf3] p-3 text-[12px] text-[#166534]">
                    <div className="font-semibold">{systemWebliumAuthResult.message}</div>
                    <div className="mt-2">Endpoint: {systemWebliumAuthResult.endpoint}</div>
                    <div className="mt-1">User: {systemWebliumAuthResult.user.username || systemWebliumAuthResult.user.id || '-'}</div>
                    <div className="mt-1">Role: {systemWebliumAuthResult.user.role || '-'}</div>
                  </div>
                )}
              </div>
            </div>
          )}
        </motion.div>
      </div>

      {blockContentPopupState.isOpen && (
        <div
          className="agent-overlay fixed inset-0 z-[140] flex items-center justify-center bg-[#0b1324]/45 p-3 sm:p-6"
          role="dialog"
          aria-modal="true"
          aria-label={blockContentPopupState.title || 'Block content'}
        >
          <div className="agent-dialog h-full w-full rounded-2xl border border-[#dbe7ff] bg-white shadow-[0_18px_60px_rgba(15,23,42,0.25)] sm:h-[95vh] sm:w-[95vw]">
            <div className="flex h-full flex-col">
              <div className="flex items-start justify-between gap-3 border-b border-[#edf0f5] px-5 py-4">
                <div>
                  <div className="text-[18px] font-bold text-[#202c42]">{blockContentPopupState.title}</div>
                  <div className="mt-1 text-[12px] text-[#5f6f89]">
                    {selectedBlockContentWebsite ? selectedBlockContentWebsite.name || selectedBlockContentWebsite.domainLabel : 'Website N/A'}
                    {' / '}
                    {selectedBlockContentPage ? selectedBlockContentPage.label || selectedBlockContentPage.name : 'Page N/A'}
                    {' / '}
                    {selectedBlockContentBlock ? getBlockDisplayName(selectedBlockContentBlock) : 'Block N/A'}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleCloseBlockContentPopup}
                  className="inline-flex size-9 shrink-0 items-center justify-center rounded-lg border border-[#dbe7ff] bg-[#f8fbff] text-[#165BFB] transition-colors hover:border-[#93b5ff] hover:bg-[#eef4ff]"
                  aria-label="Close popup"
                >
                  <svg className="size-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 6 6 18" />
                    <path d="m6 6 12 12" />
                  </svg>
                </button>
              </div>

              <div className="flex-1 overflow-hidden p-5">
                {blockContentPopupState.isLoading && (
                  <div className="flex h-full items-center justify-center rounded-xl border border-[#dbe7ff] bg-[#f8fbff] text-[14px] font-semibold text-[#4f6ea8]">
                    Loading content...
                  </div>
                )}

                {!blockContentPopupState.isLoading && blockContentPopupState.error && (
                  <div className="flex h-full items-center justify-center rounded-xl border border-[#fecaca] bg-[#fff1f2] px-4 text-[14px] text-[#b4232f]">
                    {blockContentPopupState.error}
                  </div>
                )}

                {!blockContentPopupState.isLoading && !blockContentPopupState.error && (
                  <div className="relative h-full overflow-auto rounded-xl border border-[#dbe7ff] bg-[#f8fbff]">
                    <button
                      type="button"
                      onClick={handleCopyBlockContentPopup}
                      disabled={!blockContentPopupState.content}
                      className="absolute right-3 top-3 z-10 inline-flex size-8 items-center justify-center rounded-lg border border-[#cfe0ff] bg-white/95 text-[#4f6ea8] shadow-sm backdrop-blur transition-colors hover:border-[#97b9ff] hover:text-[#165BFB] disabled:cursor-not-allowed disabled:opacity-50"
                      title={isBlockContentPopupCopied ? 'Copied' : 'Copy content'}
                      aria-label="Copy block content"
                    >
                      {isBlockContentPopupCopied ? (
                        <svg className="size-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M20 6 9 17l-5-5" />
                        </svg>
                      ) : (
                        <svg className="size-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                        </svg>
                      )}
                    </button>
                    <pre className="custom-code-preview__highlight min-h-full">
                      <code dangerouslySetInnerHTML={{ __html: highlightedBlockContentPopup }} />
                    </pre>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
